<template>
  <v-dialog :value="value" :max-width="showFinder ? '100%' : isRecurring ? '1080' : '440'" :fullscreen="showFinder" persistent>
    <v-card
      :dark="isDarkMode"
      class="wcd-card rounded-xl overflow-hidden d-flex flex-column"
      :class="{ 'light-theme': !isDarkMode }"
      :style="{
        transition: 'max-width 0.25s ease',
        height: showFinder ? '100vh' : 'auto',
        maxHeight: showFinder ? '100vh' : '90vh',
      }">
      <!-- The Header -->
      <div class="wcd-header">
        <div class="d-flex align-center" style="gap: 10px">
          <div class="wcd-icon" :style="{ background: data?.template.color || '#4CAF50' }">
            <v-icon small color="white">{{ localIcon || data?.template.icon || "mdi-calendar" }}</v-icon>
          </div>
          <div>
            <div class="wcd-title">{{ localTitle || data?.template.title }}</div>
            <div class="wcd-subtitle">{{ selectedEmployeeName }}</div>
          </div>
        </div>

        <v-btn
          icon
          x-small
          :disabled="saving"
          @click="$emit('input', false)"
          :style="{ color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.4)' }">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Content Container -->
      <div
        class="d-flex flex-grow-1 overflow-hidden"
        :class="$vuetify.breakpoint.smAndDown ? 'flex-column' : 'flex-row'"
        :style="{
          minHeight: showFinder ? '0' : '480px',
        }">
        <!-- Left Column: Booking Form -->
        <div
          class="wcd-form-column d-flex flex-column"
          :style="
            $vuetify.breakpoint.smAndDown
              ? 'width: 100%; max-height: calc(90vh - 75px);'
              : {
                  width: '440px',
                  maxWidth: '440px',
                  flexShrink: 0,
                  height: '100%',
                  maxHeight: showFinder ? '100%' : 'calc(90vh - 75px)',
                }
          ">
          <v-card-text class="pt-4 pb-2 flex-grow-1 overflow-y-auto" style="scrollbar-width: thin">
            <!-- Zeitbereich Row -->
            <div class="wcd-time-row">
              <div class="wcd-time-item">
                <div class="wcd-time-label">Start</div>
                <v-text-field
                  v-if="data"
                  v-model="localStart"
                  type="time"
                  dense
                  solo
                  flat
                  :dark="isDarkMode"
                  hide-details
                  :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                  class="wcd-time-input"></v-text-field>
              </div>
              <div class="wcd-time-item" v-if="data">
                <div class="wcd-time-label">Dauer</div>
                <v-text-field
                  v-model.number="localDuration"
                  type="number"
                  suffix="Min"
                  dense
                  solo
                  flat
                  :dark="isDarkMode"
                  hide-details
                  :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                  class="wcd-time-input"></v-text-field>
              </div>
              <div class="wcd-time-item">
                <div class="wcd-time-label">Ende</div>
                <div class="wcd-time-result">{{ calculatedEnd }}</div>
              </div>
            </div>

            <!-- Mitarbeiter Auswahl (Dropdown) -->
            <div class="mt-4" v-if="employees && employees.length > 0">
              <div class="wcd-time-label mb-1">{{ isOnlineMeeting ? "Meeting mit *" : "Mitarbeiter *" }}</div>
              <v-select
                v-model="localColId"
                :items="employeeItems"
                dense
                solo
                flat
                hide-details
                :dark="isDarkMode"
                :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                class="wcd-employee-select"></v-select>
            </div>

            <!-- Termin-Finder Toggle Button Section (Hidden on Mobile) -->
            <div class="mt-4 mb-2" v-if="!$vuetify.breakpoint.xs && canUseFinder">
              <v-btn block depressed class="wcd-finder-toggle-bar text-none" :class="{ active: showFinder }" @click="toggleFinder">
                <v-icon left size="16">mdi-calendar-search</v-icon>
                <span>{{ showFinder ? "Termin-Finder schliessen" : "Freie Termine finden (Termin-Finder)" }}</span>
                <v-icon right size="16" class="ml-auto">{{ showFinder ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              </v-btn>
            </div>

            <div v-if="isOnlineMeeting" class="wcd-tac-account-hint mt-4">
              <v-icon small color="#6264A7" class="mr-1">mdi-microsoft-teams</v-icon>
              Du nimmst als Ersteller automatisch teil. Euer Teams-Link wird beim Speichern erstellt und erscheint bei euch beiden unter Buchungen.
            </div>

            <!-- Kunde Bereich (Online Meetings verwenden automatisch den Ersteller) -->
            <div v-if="data?.template.requiresCustomer && !isOnlineMeeting" class="mt-4 relative" style="z-index: 1000">
              <div class="wcd-time-label mb-1">Kunde *</div>

              <!-- Toggle: Account vorhanden / kein Account -->
              <div v-if="!tacServiceType && !isOnlineMeeting" class="d-flex mb-3" style="gap: 4px">
                <div class="wcd-toggle-btn flex-grow-1" :class="{ active: !noAccountMode }" @click="noAccountMode = false">
                  <v-icon x-small class="mr-1">mdi-account-search</v-icon> Kunde suchen
                </div>
                <div
                  class="wcd-toggle-btn flex-grow-1"
                  :class="{ active: noAccountMode }"
                  @click="
                    noAccountMode = true
                    selectedCustomer = null
                  ">
                  <v-icon x-small class="mr-1">mdi-account-off</v-icon> Kein Account
                </div>
              </div>

              <div v-else-if="tacServiceType" class="wcd-tac-account-hint mb-3">
                <v-icon x-small color="primary" class="mr-1">mdi-account-check</v-icon>
                Für die TAC-Kartensuche ist ein Kundenkonto erforderlich.
              </div>

              <!-- Modus 1: Normaler Kunden-Suche -->
              <template v-if="!noAccountMode">
                <user-search
                  :value="selectedCustomer"
                  @update="updateCustomer"
                  return-user
                  ignore-rights
                  label="Kunden suchen..."
                  :dark="isDarkMode"
                  outlined
                  dense
                  :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
              </template>

              <!-- Modus 2: Kein Account – Manuelle Eingabe -->
              <template v-else>
                <div class="d-flex gap-2">
                  <v-text-field v-model="noAccountInfo.firstName" label="Vorname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                  <v-text-field v-model="noAccountInfo.lastName" label="Nachname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                </div>
                <v-text-field
                  v-model="noAccountInfo.phone"
                  label="Telefon *"
                  outlined
                  dense
                  :dark="isDarkMode"
                  hide-details
                  class="mt-2"
                  prepend-inner-icon="mdi-phone"></v-text-field>
                <v-text-field
                  v-model="noAccountInfo.email"
                  label="E-Mail (Optional)"
                  outlined
                  dense
                  :dark="isDarkMode"
                  hide-details
                  class="mt-2"
                  prepend-inner-icon="mdi-email-outline"></v-text-field>
              </template>
            </div>

            <div v-if="tacServiceType" class="wcd-tac-credit-card mt-3">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="wcd-time-label mb-0">
                  <v-icon x-small color="primary" class="mr-1">mdi-ticket-confirmation-outline</v-icon>
                  {{ tacServiceLabel }}-Guthaben
                </div>
                <v-progress-circular v-if="tacCreditsLoading" indeterminate size="16" width="2" color="primary"></v-progress-circular>
                <v-chip v-else-if="tacCreditCards.length" x-small color="primary" outlined>
                  {{ tacCreditCards.length }} Package{{ tacCreditCards.length === 1 ? "" : "s" }}
                </v-chip>
              </div>

              <div v-if="selectedCustomer" class="d-flex justify-end mb-2">
                <v-btn x-small text color="primary" class="text-none" :loading="tacPackageHistoryLoading" @click="loadTacPackageHistoryDebug">
                  Package-History testen
                </v-btn>
              </div>
              <div v-if="!selectedCustomer" class="caption grey--text">Bitte zuerst einen Kunden auswählen.</div>
              <v-alert v-else-if="tacCreditsError" dense text type="error" class="mb-0 caption">
                {{ tacCreditsError }}
                <v-btn x-small text color="error" class="ml-1" @click="loadTacCredits">Erneut laden</v-btn>
              </v-alert>
              <div v-else-if="tacCreditsLoading" class="caption grey--text">TAC-Karten werden geladen ...</div>
              <v-alert v-else-if="!tacCreditCards.length" dense text type="warning" class="mb-0 caption">
                Für diesen Kunden wurde kein noch verwendbares {{ tacServiceLabel }}-Package gefunden.
              </v-alert>
              <template v-else>
                <v-select
                  v-model="selectedTacPackageId"
                  :items="tacCreditItems"
                  label="TAC-Karte"
                  outlined
                  dense
                  hide-details
                  :dark="isDarkMode"
                  :disabled="tacCreditsLoading"></v-select>
                <v-alert v-if="!tacBillingEnabled" dense text type="info" class="mt-2 mb-0 caption">
                  Die TAC-Prüfung funktioniert. Zum tatsächlichen Buchen muss
                  <code>appointmentBillingEnabled</code> im Backend aktiviert werden.
                </v-alert>
                <v-alert v-else-if="selectedTacCard?.exactAvailable && tacEmployeeMatches" dense text type="success" class="mt-2 mb-0 caption">
                  {{ localStart }}–{{ calculatedEnd }} ist in TAC verfügbar <span v-if="selectedTacResource"> bei {{ selectedTacResource.name }}</span
                  >. Beim Speichern wird eine Package-Leistung gebucht.
                </v-alert>
                <v-alert v-else-if="selectedTacCard?.exactAvailable" dense text type="warning" class="mt-2 mb-0 caption">
                  TAC hat diesen Termin bei {{ selectedTacResource?.name || "einem anderen Mitarbeiter" }} gefunden.
                  <v-btn x-small text color="warning" class="ml-1" @click="applyTacResourceEmployee(selectedTacResource)">
                    Mitarbeiter übernehmen
                  </v-btn>
                </v-alert>
                <v-alert v-else dense text type="warning" class="mt-2 mb-0 caption"> Der gewählte Zeitpunkt ist in TAC nicht verfügbar. </v-alert>

                <div v-if="selectedTacAlternatives.length" class="mt-2">
                  <div class="caption grey--text mb-1">TAC-Alternativen am {{ formatDate(selectedDate) }}:</div>
                  <div class="d-flex flex-wrap" style="gap: 6px">
                    <v-btn
                      v-for="slot in selectedTacAlternatives"
                      :key="`${slot.packageId}-${slot.start}-${slot.resources && slot.resources[0] ? slot.resources[0].id : ''}`"
                      x-small
                      outlined
                      color="primary"
                      class="text-none"
                      @click="applyTacAvailability(slot)">
                      {{ slot.start }}–{{ slot.end }}
                      <span v-if="slot.resources && slot.resources[0] && slot.resources[0].name">&nbsp;· {{ slot.resources[0].name }}</span>
                    </v-btn>
                  </div>
                </div>
              </template>
            </div>

            <!-- Zweite Person hinzufügen (nur bei requiresCustomer) -->
            <div v-if="data?.template.requiresCustomer && !tacServiceType && !isOnlineMeeting && (selectedCustomer || noAccountMode)" class="mt-3">
              <div v-if="!showSecondPerson" class="d-flex align-center">
                <v-btn text x-small color="primary" @click="addSecondPerson" class="text-none">
                  <v-icon left x-small>mdi-account-plus</v-icon> Zweite Person hinzufügen (+30 Min)
                </v-btn>
              </div>
              <div v-else>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="wcd-time-label">2. Person</div>
                  <v-btn icon x-small @click="removeSecondPerson" style="color: rgba(255, 255, 255, 0.3)">
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>

                <div class="d-flex mb-2" style="gap: 4px">
                  <div class="wcd-toggle-btn flex-grow-1" :class="{ active: !noAccountMode2 }" @click="noAccountMode2 = false">
                    <v-icon x-small class="mr-1">mdi-account-search</v-icon> Suchen
                  </div>
                  <div
                    class="wcd-toggle-btn flex-grow-1"
                    :class="{ active: noAccountMode2 }"
                    @click="
                      noAccountMode2 = true
                      selectedCustomer2 = null
                    ">
                    <v-icon x-small class="mr-1">mdi-account-off</v-icon> Kein Account
                  </div>
                </div>

                <template v-if="!noAccountMode2">
                  <user-search
                    :value="selectedCustomer2"
                    @update="updateCustomer2"
                    return-user
                    ignore-rights
                    label="2. Kunden suchen..."
                    :dark="isDarkMode"
                    outlined
                    dense
                    :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
                </template>
                <template v-else>
                  <div class="d-flex gap-2">
                    <v-text-field v-model="noAccountInfo2.firstName" label="Vorname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                    <v-text-field v-model="noAccountInfo2.lastName" label="Nachname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                  </div>
                  <v-text-field
                    v-model="noAccountInfo2.phone"
                    label="Telefon *"
                    outlined
                    dense
                    :dark="isDarkMode"
                    hide-details
                    class="mt-2"
                    prepend-inner-icon="mdi-phone"></v-text-field>
                  <v-text-field
                    v-model="noAccountInfo2.email"
                    label="E-Mail (Optional)"
                    outlined
                    dense
                    :dark="isDarkMode"
                    hide-details
                    class="mt-2"
                    prepend-inner-icon="mdi-email-outline"></v-text-field>
                </template>

                <div class="wcd-dual-badge mt-2">
                  <v-icon x-small color="primary" class="mr-1">mdi-account-multiple</v-icon>
                  <span>Dauer +30 Min ({{ localDuration }} Min total)</span>
                </div>
              </div>
            </div>

            <!-- New Contract Section -->
            <div v-if="data?.template.specialType === 'new_contract'" class="mt-4 relative" style="z-index: 1000">
              <div class="wcd-time-label mb-1">Kunde *</div>

              <!-- Toggle: Neuer Kunde / Bestehender Kunde -->
              <div class="d-flex mb-3" style="gap: 4px">
                <div
                  class="wcd-toggle-btn flex-grow-1"
                  :class="{ active: contractMode === 'guest' }"
                  @click="
                    contractMode = 'guest'
                    selectedCustomer = null
                  ">
                  <v-icon x-small class="mr-1">mdi-account-plus</v-icon> Neuer Kunde (Gast)
                </div>
                <div
                  class="wcd-toggle-btn flex-grow-1"
                  :class="{ active: contractMode === 'existing' }"
                  @click="
                    contractMode = 'existing'
                    guestData = { firstName: '', lastName: '', email: '' }
                  ">
                  <v-icon x-small class="mr-1">mdi-account-search</v-icon> Bestehender Kunde
                </div>
              </div>

              <!-- Modus 1: Neuer Kunde (Gast) -->
              <template v-if="contractMode === 'guest'">
                <div class="d-flex gap-2">
                  <v-text-field v-model="guestData.firstName" label="Vorname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                  <v-text-field v-model="guestData.lastName" label="Nachname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                </div>
                <v-text-field
                  v-model="guestData.email"
                  label="E-Mail (Optional)"
                  outlined
                  dense
                  :dark="isDarkMode"
                  hide-details
                  class="mt-2"
                  prepend-inner-icon="mdi-email-outline"></v-text-field>
              </template>

              <!-- Modus 2: Bestehender Kunde -->
              <template v-else>
                <user-search
                  :value="selectedCustomer"
                  @update="updateCustomer"
                  return-user
                  ignore-rights
                  label="Kunden suchen..."
                  :dark="isDarkMode"
                  outlined
                  dense
                  :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
              </template>
            </div>

            <!-- Titel-Eingabe falls freier Termin (ap1) -->
            <div v-if="data?.template?.id === 'ap1' || isOnlineMeeting" class="mt-4">
              <div class="wcd-time-label mb-1">Titel *</div>
              <v-text-field v-model="localTitle" outlined dense :dark="isDarkMode" hide-details placeholder="Termintitel eingeben..."></v-text-field>
            </div>

            <!-- Icon-Auswahl falls freier Termin (ap1) -->
            <div v-if="data?.template?.id === 'ap1' && !isOnlineMeeting" class="mt-3">
              <div class="wcd-time-label mb-2">Icon</div>
              <div class="d-flex flex-wrap gap-2">
                <v-btn
                  v-for="ic in ['mdi-calendar', 'mdi-account-group', 'mdi-phone', 'mdi-laptop', 'mdi-dumbbell', 'mdi-heart-pulse']"
                  :key="ic"
                  icon
                  small
                  :color="localIcon === ic ? 'primary' : 'grey'"
                  style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1)"
                  :style="localIcon === ic ? 'border-color: var(--v-primary-base)' : ''"
                  @click="localIcon = ic">
                  <v-icon small>{{ ic }}</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Serien-Termin / Wiederholung -->
            <div class="mt-4" v-if="data && !tacServiceType && !isOnlineMeeting">
              <v-divider class="mb-4" style="opacity: 0.05"></v-divider>
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="wcd-time-label mb-0" style="font-size: 0.7rem; letter-spacing: 0.5px">Termin wiederholen (Serie)</div>
                <v-switch v-model="isRecurring" dense hide-details class="mt-0 pt-0" color="primary"></v-switch>
              </div>

              <v-expand-transition>
                <div v-if="isRecurring" class="wcd-recurring-panel pa-3 rounded-lg mt-2 mb-2">
                  <!-- Frequenz -->
                  <div class="mb-3">
                    <div class="wcd-small-label">Frequenz</div>
                    <v-btn-toggle v-model="recurrence.frequency" mandatory dense class="wcd-toggle-group">
                      <v-btn value="daily" class="wcd-toggle-btn-small">Täglich</v-btn>
                      <v-btn value="weekly" class="wcd-toggle-btn-small">Wöchentlich</v-btn>
                      <v-btn value="monthly" class="wcd-toggle-btn-small">Monatlich</v-btn>
                    </v-btn-toggle>
                  </div>

                  <!-- Intervall-Zahl -->
                  <div class="d-flex align-center mb-3" style="gap: 12px">
                    <div style="width: 80px">
                      <div class="wcd-small-label">Intervall</div>
                      <v-text-field
                        v-model.number="recurrence.interval"
                        type="number"
                        min="1"
                        dense
                        solo
                        flat
                        hide-details
                        :dark="isDarkMode"
                        :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                        class="wcd-numeric-input"></v-text-field>
                    </div>
                    <div class="flex-grow-1 pt-4" style="font-size: 0.75rem; font-weight: 600; opacity: 0.7">
                      Alle {{ recurrence.interval || 1 }}
                      {{ recurrence.frequency === "daily" ? "Tag(e)" : recurrence.frequency === "weekly" ? "Woche(n)" : "Monat(e)" }}
                    </div>
                  </div>

                  <!-- Wochentage (nur bei weekly) -->
                  <div v-if="recurrence.frequency === 'weekly'" class="mb-3">
                    <div class="wcd-small-label mb-1">An folgenden Tagen</div>
                    <div class="d-flex justify-space-between" style="gap: 4px">
                      <v-btn
                        v-for="(day, idx) in weekdayOptions"
                        :key="idx"
                        x-small
                        fab
                        depressed
                        :color="recurrence.weekdays.includes(day.value) ? 'primary' : isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'"
                        :class="
                          recurrence.weekdays.includes(day.value)
                            ? 'white--text font-weight-bold'
                            : isDarkMode
                            ? 'grey--text text--lighten-1'
                            : 'grey--text text--darken-2'
                        "
                        @click="toggleWeekday(day.value)"
                        style="width: 28px; height: 28px; min-width: 28px; font-size: 0.7rem">
                        {{ day.text }}
                      </v-btn>
                    </div>
                  </div>

                  <!-- Seriendauer / Ende -->
                  <div class="mb-2">
                    <div class="wcd-small-label mb-2">Seriendauer</div>

                    <v-radio-group v-model="recurrenceEndType" dense hide-details class="mt-0 pt-0 wcd-radio-group">
                      <div class="d-flex flex-column gap-2">
                        <!-- Nach X Terminen -->
                        <div class="d-flex align-center" style="gap: 8px">
                          <v-radio value="count" color="primary" dense class="mr-0"></v-radio>
                          <span style="font-size: 0.75rem; opacity: 0.8">Endet nach</span>
                          <v-text-field
                            v-model.number="recurrence.count"
                            :disabled="recurrenceEndType !== 'count'"
                            type="number"
                            min="1"
                            dense
                            solo
                            flat
                            hide-details
                            :dark="isDarkMode"
                            :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                            style="width: 60px; max-width: 60px"
                            class="wcd-numeric-input"></v-text-field>
                          <span style="font-size: 0.75rem; opacity: 0.8">Terminen</span>
                        </div>

                        <!-- Am Enddatum -->
                        <div class="d-flex align-center mt-1" style="gap: 8px">
                          <v-radio value="date" color="primary" dense class="mr-0"></v-radio>
                          <span style="font-size: 0.75rem; opacity: 0.8">Endet am</span>

                          <v-menu v-model="showDatePicker" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                :value="formatDate(recurrence.endDate)"
                                :disabled="recurrenceEndType !== 'date'"
                                readonly
                                dense
                                solo
                                flat
                                hide-details
                                v-bind="attrs"
                                v-on="on"
                                :dark="isDarkMode"
                                :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                                style="width: 120px"
                                class="wcd-date-input">
                                <template v-slot:append>
                                  <v-icon small :disabled="recurrenceEndType !== 'date'">mdi-calendar</v-icon>
                                </template>
                              </v-text-field>
                            </template>
                            <v-date-picker v-model="recurrence.endDate" @input="showDatePicker = false" no-title scrollable></v-date-picker>
                          </v-menu>
                        </div>
                      </div>
                    </v-radio-group>
                  </div>

                  <!-- Uhrzeit-Flexibilität -->
                  <div class="mt-3">
                    <div class="wcd-small-label mb-1">Uhrzeit-Flexibilität</div>
                    <v-select
                      v-model="recurrenceFlexibility"
                      :items="flexibilityOptions"
                      dense
                      solo
                      flat
                      hide-details
                      :dark="isDarkMode"
                      :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                      class="wcd-employee-select"></v-select>

                    <v-expand-transition>
                      <div v-if="recurrenceFlexibility === 'window'" class="d-flex align-center mt-2" style="gap: 8px">
                        <span style="font-size: 0.75rem; opacity: 0.8">Bereich: ±</span>
                        <v-text-field
                          v-model.number="recurrenceFlexibilityWindow"
                          type="number"
                          min="1"
                          max="12"
                          dense
                          solo
                          flat
                          hide-details
                          :dark="isDarkMode"
                          :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                          style="width: 60px; max-width: 60px"
                          class="wcd-numeric-input"></v-text-field>
                        <span style="font-size: 0.75rem; opacity: 0.8">Stunden</span>
                      </div>
                    </v-expand-transition>
                  </div>
                </div>
              </v-expand-transition>
            </div>

            <!-- Notes Textarea -->
            <v-textarea
              v-if="data"
              v-model="localDesc"
              :label="noteLabel"
              outlined
              dense
              rows="2"
              :dark="isDarkMode"
              :hide-details="!isNoteRequired || !!localDesc"
              :error="isNoteRequired && !localDesc"
              :error-messages="isNoteRequired && !localDesc ? ['Pflichtfeld – bitte Notiz eingeben'] : []"
              class="mt-4"
              style="font-size: 0.85rem"></v-textarea>
          </v-card-text>

          <v-divider style="opacity: 0.05"></v-divider>

          <!-- Left Card Actions -->
          <v-card-actions class="px-5 pb-4 pt-4">
            <v-spacer></v-spacer>
            <v-btn
              text
              small
              :disabled="saving"
              @click="$emit('input', false)"
              :style="{ color: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.5)', textTransform: 'none' }"
              >Abbrechen</v-btn
            >
            <v-btn small depressed class="wcd-save-btn" :loading="saving" @click="save" :disabled="saving || !canSave">
              <v-icon left x-small>mdi-check</v-icon> Erstellen
            </v-btn>
          </v-card-actions>
        </div>

        <!-- Right Column: Recurrence Preview Panel -->
        <div
          v-if="isRecurring && !showFinder"
          class="wcd-preview-column d-flex flex-column flex-grow-1"
          :style="
            $vuetify.breakpoint.smAndDown
              ? 'width: 100%; border-top: 1px solid rgba(255,255,255,0.08);'
              : {
                  borderLeft: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0, 0, 0, 0.01)',
                  height: '100%',
                  minWidth: '560px',
                  maxWidth: '640px',
                  maxHeight: showFinder ? '100%' : 'calc(90vh - 75px)',
                }
          ">
          <!-- Preview Header / Controls -->
          <div class="pa-4 d-flex flex-column" style="gap: 12px; border-bottom: 1px solid rgba(255, 255, 255, 0.05)">
            <div class="d-flex align-center justify-space-between">
              <div class="wcd-title" style="font-size: 0.95rem">Serien-Vorschau</div>
              <v-progress-circular v-if="loadingPreview" indeterminate size="16" width="2" color="primary"></v-progress-circular>
            </div>

            <!-- Global Conflict Rule Selector -->
            <div>
              <div class="wcd-small-label mb-1">Konflikt-Regel (Serientermine)</div>
              <v-select
                v-model="globalConflictRule"
                :items="conflictRules"
                dense
                solo
                flat
                hide-details
                :dark="isDarkMode"
                :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                class="wcd-employee-select"></v-select>
            </div>
          </div>

          <!-- Preview Items List -->
          <div class="flex-grow-1 overflow-y-auto pa-4" style="scrollbar-width: thin; max-height: calc(90vh - 200px)">
            <div v-if="!previewList || !previewList.length" class="text-center py-8 grey--text text--darken-1" style="font-size: 0.85rem">
              Keine Vorschau verfügbar. Bitte überprüfen Sie die Frequenz- und Datumseinstellungen.
            </div>

            <div v-else class="d-flex flex-column" style="gap: 10px">
              <div
                v-for="(item, idx) in previewList"
                :key="idx"
                class="wcd-preview-item pa-3 rounded-lg d-flex align-center justify-space-between"
                :class="{
                  skipped: item.skipped,
                  'light-theme-item': !isDarkMode,
                }"
                :style="getPreviewItemStyle(item)">
                <!-- Left: Date & Day & Status -->
                <div class="d-flex flex-column" style="gap: 2px; max-width: 320px">
                  <div class="d-flex align-center" style="gap: 6px">
                    <v-checkbox v-model="item.skipped" dense hide-details class="mt-0 pt-0 mr-1" color="error" style="margin-top: 0px"></v-checkbox>
                    <span
                      class="font-weight-bold"
                      style="font-size: 0.8rem"
                      :style="{ textDecoration: item.skipped ? 'line-through' : 'none', opacity: item.skipped ? 0.5 : 1 }">
                      {{ formatPreviewDate(item.date) }}
                    </span>
                  </div>

                  <!-- Status Chip / Text -->
                  <div v-if="!item.skipped" class="d-flex align-center mt-1" style="gap: 4px; flex-wrap: wrap">
                    <template v-if="item.isAdjusted">
                      <v-icon x-small color="success">mdi-clock-outline</v-icon>
                      <span class="success--text font-weight-bold" style="font-size: 0.7rem">
                        Verschoben: {{ item.adjustedTime }} (statt {{ item.originalTime }})
                      </span>
                    </template>
                    <template v-else-if="item.status === 'available'">
                      <v-icon x-small color="success">mdi-check-circle</v-icon>
                      <span class="success--text font-weight-medium" style="font-size: 0.7rem">Verfügbar</span>
                    </template>
                    <template v-else-if="item.status === 'collision'">
                      <v-icon x-small color="warning">mdi-alert-circle</v-icon>
                      <span
                        class="warning--text font-weight-medium"
                        style="font-size: 0.7rem; cursor: help"
                        :title="getCollisionTooltip(item.conflict)">
                        Kollision: {{ item.conflict?.title || "Eintrag" }}
                      </span>
                    </template>
                    <template v-else-if="item.status === 'no_shiftplan'">
                      <v-icon x-small color="warning">mdi-calendar-blank</v-icon>
                      <span class="warning--text font-weight-medium" style="font-size: 0.7rem">Schichtplan fehlt</span>
                    </template>
                    <template v-else-if="item.status === 'absent'">
                      <v-icon x-small color="error">mdi-close-circle</v-icon>
                      <span class="error--text font-weight-medium" style="font-size: 0.7rem">Abwesend</span>
                    </template>
                  </div>
                  <div v-else class="grey--text font-weight-medium" style="font-size: 0.7rem; opacity: 0.6">Übersprungen</div>
                </div>

                <!-- Right: Employee & Time Selector -->
                <div class="d-flex align-center" style="gap: 8px" v-if="!item.skipped">
                  <!-- Time Select -->
                  <div style="width: 100px">
                    <v-select
                      :value="item.adjustedTime || item.originalTime"
                      :items="getAvailableTimeOptions(item)"
                      @change="onPreviewTimeChange(item, $event)"
                      dense
                      solo
                      flat
                      hide-details
                      :dark="isDarkMode"
                      :background-color="isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'"
                      style="font-size: 0.75rem"
                      class="wcd-preview-time-select"></v-select>
                  </div>

                  <!-- Employee Select -->
                  <div style="width: 240px">
                    <v-select
                      v-model="item.selectedEmployeeId"
                      :items="getPreviewEmployeeOptions(item)"
                      @change="onPreviewEmployeeChange(item)"
                      dense
                      solo
                      flat
                      hide-details
                      :dark="isDarkMode"
                      :background-color="isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'"
                      class="wcd-preview-employee-select"
                      style="font-size: 0.75rem">
                      <template v-slot:selection="{ item }">
                        <span v-if="item" style="font-size: 0.75rem" :class="item.class">{{ item.text }}</span>
                      </template>
                      <template v-slot:item="{ item }">
                        <span v-if="item" style="font-size: 0.75rem" :class="item.class">{{ item.text }}</span>
                      </template>
                    </v-select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Finder Dashboard (Desktop only) -->
        <work-calendar-appointment-finder
          v-if="showFinder && canUseFinder && !$vuetify.breakpoint.smAndDown"
          class="wcd-finder-section flex-grow-1"
          :style="{
            borderLeft: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
            background: isDarkMode ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0, 0, 0, 0.01)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }"
          :employees="eligibleEmployees"
          :date="selectedDate"
          @update:date="selectedDate = $event"
          :start="localStart"
          @update:start="localStart = $event"
          :col-id="localColId"
          @update:col-id="localColId = $event"
          :duration="localDuration"
          :is-dark-mode="isDarkMode"
          @select-slot="onFinderSelectSlot"
          @select-and-close="onFinderSelectAndClose" />
      </div>

      <!-- Mobile Expandable Finder Section (Tablet only, disabled on Mobile Phone) -->
      <v-expand-transition v-if="$vuetify.breakpoint.smAndDown && !$vuetify.breakpoint.xs">
        <work-calendar-appointment-finder
          v-if="showFinder && canUseFinder"
          class="wcd-mobile-finder-section pa-3"
          style="
            border-top: 1px solid rgba(255, 255, 255, 0.08);
            background: rgba(255, 255, 255, 0.01);
            height: 450px;
            display: flex;
            flex-direction: column;
          "
          :employees="eligibleEmployees"
          :date="selectedDate"
          @update:date="selectedDate = $event"
          :start="localStart"
          @update:start="localStart = $event"
          :col-id="localColId"
          @update:col-id="localColId = $event"
          :duration="localDuration"
          :is-dark-mode="isDarkMode"
          @select-slot="onFinderSelectSlot"
          @select-and-close="onFinderSelectAndClose" />
      </v-expand-transition>
    </v-card>
  </v-dialog>
</template>

<script>
import UserSearch from "@/components/userSearch"
import WorkCalendarAppointmentFinder from "./WorkCalendarAppointmentFinder"
import moment from "moment"
import api from "@/api"
import userService from "@/services/userService"
import { isOnlineMeeting } from "@/services/onlineMeeting"

export default {
  name: "WorkCalendarDropDialog",
  components: { UserSearch, WorkCalendarAppointmentFinder },
  props: {
    value: { type: Boolean, default: false },
    data: { type: Object, default: null },
    employees: { type: Array, default: () => [] },
    currentDate: { type: String, default: "" },
    isDarkMode: { type: Boolean, default: true },
    tacAppointmentBillingActive: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      localColId: null,
      localStart: "",
      localDuration: 60,
      localDesc: "",
      localTitle: "",
      localIcon: "",
      selectedCustomer: null,
      guestData: {
        firstName: "",
        lastName: "",
        email: "",
      },
      noAccountMode: false,
      noAccountInfo: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
      showSecondPerson: false,
      selectedCustomer2: null,
      noAccountMode2: false,
      noAccountInfo2: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
      baseDuration: 60,
      contractMode: "guest",
      tacCreditsLoading: false,
      tacCreditsError: "",
      tacCreditsUnfiltered: false,
      tacCreditCards: [],
      selectedTacPackageId: null,
      tacActivityTemplateId: null,
      tacBillingEnabled: false,
      tacCreditsRequestId: 0,
      tacCreditsTimeout: null,
      tacBookingRequestId: "",
      tacPackageHistoryLoading: false,

      showFinder: false,
      selectedDate: null,
      finderSelection: null,
      finderSelectedEmployee: null,

      // Recurrence (Series) Properties
      isRecurring: false,
      recurrenceEndType: "count",
      recurrence: {
        frequency: "daily",
        interval: 1,
        weekdays: [],
        endDate: "",
        count: 10,
      },
      showDatePicker: false,
      weekdayOptions: [
        { text: "Mo", value: 1 },
        { text: "Di", value: 2 },
        { text: "Mi", value: 3 },
        { text: "Do", value: 4 },
        { text: "Fr", value: 5 },
        { text: "Sa", value: 6 },
        { text: "So", value: 7 },
      ],
      // Preview fields
      previewList: [],
      loadingPreview: false,
      globalConflictRule: "same",
      recurrenceFlexibility: "fixed",
      recurrenceFlexibilityWindow: 2,
      flexibilityOptions: [
        { text: "Feste Uhrzeit", value: "fixed" },
        { text: "Beliebige Uhrzeit (Egal)", value: "any" },
        { text: "Flexibel (± Stunden)", value: "window" },
      ],
    }
  },
  watch: {
    data: {
      immediate: true,
      handler(val) {
        if (val) {
          this.localColId = val.col?.id || null
          this.localStart = val.time
          this.baseDuration = val.template.duration || 60
          this.localDuration = this.baseDuration
          this.localDesc = val.description || ""
          this.localTitle = val.template.title || ""
          this.localIcon = val.template.icon || "mdi-calendar"

          // Pre-fill customer/account info if extending or editing
          this.selectedCustomer = val.customer || null
          this.selectedCustomer2 = val.customer2 || null
          this.noAccountMode = !!val.noAccountData
          this.noAccountMode2 = !!val.noAccountData2
          this.showSecondPerson = !!val.customer2 || !!val.noAccountData2
          this.noAccountInfo = val.noAccountData ? { ...val.noAccountData } : { firstName: "", lastName: "", phone: "", email: "" }
          this.noAccountInfo2 = val.noAccountData2 ? { ...val.noAccountData2 } : { firstName: "", lastName: "", phone: "", email: "" }
          this.guestData = val.guestData ? { ...val.guestData } : { firstName: "", lastName: "", email: "" }
          this.contractMode = val.guestData ? "guest" : "existing"

          this.selectedDate = val.date || this.currentDate || moment().format("YYYY-MM-DD")
          this.showFinder = false
          this.finderSelection = null
          this.finderSelectedEmployee = null

          // Reset Recurrence Defaults
          this.isRecurring = !!val.isExtend
          this.recurrenceEndType = "count"
          const initialDate = this.selectedDate
          this.recurrence = {
            frequency: "daily",
            interval: 1,
            weekdays: [moment(initialDate).isoWeekday()],
            endDate: moment(initialDate).add(1, "month").format("YYYY-MM-DD"),
            count: 10,
          }
          this.recurrenceFlexibility = "fixed"
          this.recurrenceFlexibilityWindow = 2
          this.resetTacCredits()
          this.tacBookingRequestId = this.createTacRequestId()
          if (this.isOnlineMeeting) {
            this.selectedCustomer = null
            this.noAccountMode = false
            this.noAccountMode2 = false
            this.selectedCustomer2 = null
            this.showSecondPerson = false
            this.isRecurring = false
          }
          if (this.tacServiceType) {
            this.noAccountMode = false
            this.showSecondPerson = false
            this.isRecurring = false
            if (this.selectedCustomerId) this.$nextTick(() => this.loadTacCredits())
          }
          this.$nextTick(() => this.ensureEligibleEmployeeSelection())
        }
      },
    },
    value(val) {
      if (val) {
        this.selectedDate = this.data?.date || this.currentDate || moment().format("YYYY-MM-DD")
        this.showFinder = false
        this.finderSelection = null
        this.finderSelectedEmployee = null
      }
    },
    isRecurring(val) {
      if (val) {
        this.fetchPreviewDebounced()
      } else {
        this.previewList = []
      }
    },
    localStart() {
      this.fetchPreviewDebounced()
      if (this.tacServiceType && this.selectedCustomerId) this.loadTacCreditsDebounced()
    },
    localDuration() {
      this.fetchPreviewDebounced()
    },
    localColId() {
      this.fetchPreviewDebounced()
    },
    selectedDate() {
      this.fetchPreviewDebounced()
      if (this.tacServiceType && this.selectedCustomerId) this.loadTacCreditsDebounced()
    },
    recurrenceEndType() {
      this.fetchPreviewDebounced()
    },
    "recurrence.frequency"() {
      this.fetchPreviewDebounced()
    },
    "recurrence.interval"() {
      this.fetchPreviewDebounced()
    },
    "recurrence.endDate"() {
      this.fetchPreviewDebounced()
    },
    "recurrence.count"() {
      this.fetchPreviewDebounced()
    },
    "recurrence.weekdays": {
      deep: true,
      handler() {
        this.fetchPreviewDebounced()
      },
    },
    globalConflictRule() {
      this.applyGlobalConflictRule()
    },
    recurrenceFlexibility() {
      this.fetchPreviewDebounced()
    },
    recurrenceFlexibilityWindow() {
      this.fetchPreviewDebounced()
    },
  },
  computed: {
    isOnlineMeeting() { return isOnlineMeeting(this.data?.template) },
    canUseFinder() {
      const template = this.data?.template || {}
      return template.type !== "task" && template.type !== "pause" && template.isPause !== true && template.specialType !== "pause"
    },
    calculatedEnd() {
      if (!this.localStart) return "-"
      return moment(this.localStart, "HH:mm").add(this.localDuration, "minutes").format("HH:mm")
    },
    employeeItems() {
      const items = this.eligibleEmployees.map((emp) => ({
        text: emp.name || emp.firstName || "Mitarbeiter",
        value: emp.id,
      }))
      if (
        this.finderSelectedEmployee &&
        this.isEmployeeEligible(this.finderSelectedEmployee) &&
        !items.some((item) => String(item.value) === String(this.finderSelectedEmployee.id))
      ) {
        items.push({ text: this.finderSelectedEmployee.name || "Mitarbeiter", value: this.finderSelectedEmployee.id })
      }
      return items
    },
    eligibleEmployees() {
      return (this.employees || []).filter((employee) => this.isEmployeeEligible(employee))
    },
    isTouchDevice() {
      if (typeof window === "undefined") return false
      return "ontouchstart" in window || navigator.maxTouchPoints > 0
    },
    selectedEmployeeName() {
      if (!this.employees || !this.localColId) return this.data?.col?.firstName || "Mitarbeiter"
      const emp = this.employees.find((e) => e.id === this.localColId)
      if (emp) return emp.name
      if (this.finderSelectedEmployee && String(this.finderSelectedEmployee.id) === String(this.localColId)) return this.finderSelectedEmployee.name
      return this.data?.col?.firstName || "Mitarbeiter"
    },
    tacServiceType() {
      if (this.isOnlineMeeting) return null
      // Die Vorlage darf eine spaetere TAC-Zuordnung behalten, ohne den normalen
      // Kalendertermin heute an Kartenabfrage oder Abbuchung zu koppeln.
      if (!this.tacAppointmentBillingActive) return null
      const configured = this.data?.template?.tacServiceType
      if (configured === "personal_training" || configured === "massage") return configured
      if (this.data?.template && Object.prototype.hasOwnProperty.call(this.data.template, "tacServiceType") && configured === null) return null
      const title = String(this.data?.template?.title || "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim()
      if (title.includes("personal training") || title === "personaltraining") return "personal_training"
      if (title.includes("massage")) return "massage"
      return null
    },
    tacServiceLabel() {
      return this.tacServiceType === "massage" ? "Massage" : "Personal Training"
    },
    selectedCustomerId() {
      const customer = this.selectedCustomer
      if (!customer) return ""
      if (typeof customer === "string" || typeof customer === "number") return String(customer)
      return String(customer._id || customer.id || customer.user?._id || customer.user || "")
    },
    selectedCustomerTacProfileId() {
      const customer = this.selectedCustomer
      if (!customer || typeof customer !== "object") return ""
      return String(customer.tac_user_id || customer.tacUserId || customer.user?.tac_user_id || "").trim()
    },
    selectedCustomerEmail() {
      const customer = this.selectedCustomer
      if (!customer || typeof customer !== "object") return ""
      return String(customer.email || customer.mail || customer.user?.email || "").trim()
    },
    tacCreditItems() {
      return this.tacCreditCards.map((card) => {
        const status = card.exactAvailable
          ? "jetzt buchbar"
          : card.alternatives?.length
          ? `${card.alternatives.length} Alternative${card.alternatives.length === 1 ? "" : "n"}`
          : "heute kein freier Termin"
        return {
          text: `${card.name} · ${card.usedBookings || 0} verwendet · ${status}`,
          value: card.packageId,
        }
      })
    },
    selectedTacCard() {
      return this.tacCreditCards.find((card) => String(card.packageId) === String(this.selectedTacPackageId)) || null
    },
    selectedTacAlternatives() {
      return this.selectedTacCard?.alternatives || []
    },
    selectedTacResource() {
      return this.selectedTacCard?.exactAvailability?.resources?.[0] || null
    },
    tacEmployeeMatches() {
      if (!this.selectedTacResource?.name) return true
      return this.normalizeTacPersonName(this.selectedTacResource.name) === this.normalizeTacPersonName(this.selectedEmployeeName)
    },
    canSave() {
      if (this.isOnlineMeeting && (!userService.hasRight("stat_admin") || !userService.getUserId() || !this.localTitle.trim() || this.noAccountMode || this.showSecondPerson || this.isRecurring)) return false
      // 1. General validations (Customer, special types, notes, titles)
      if (this.tacServiceType) {
        if (this.noAccountMode || this.showSecondPerson || this.isRecurring || !this.selectedCustomerId) return false
        if (
          this.tacCreditsLoading ||
          !this.tacBillingEnabled ||
          !this.selectedTacCard ||
          !this.selectedTacCard.exactAvailable ||
          !this.tacEmployeeMatches
        ) {
          return false
        }
      }
      if (this.data?.template.requiresCustomer && !this.isOnlineMeeting) {
        const hasCustomer1 = this.noAccountMode
          ? this.noAccountInfo.firstName && this.noAccountInfo.lastName && this.noAccountInfo.phone
          : !!this.selectedCustomer
        if (!hasCustomer1) return false

        if (this.showSecondPerson) {
          const hasCustomer2 = this.noAccountMode2
            ? this.noAccountInfo2.firstName && this.noAccountInfo2.lastName && this.noAccountInfo2.phone
            : !!this.selectedCustomer2
          if (!hasCustomer2) return false
        }
      }

      if (this.data?.template.specialType === "new_contract") {
        if (this.contractMode === "guest" && (!this.guestData.firstName || !this.guestData.lastName)) {
          return false
        }
        if (this.contractMode === "existing" && !this.selectedCustomer) {
          return false
        }
      }

      if (this.data?.template?.id === "ap1" && !this.localTitle.trim()) {
        return false
      }

      if (this.isNoteRequired && !this.localDesc.trim()) {
        return false
      }

      // 2. Mode-specific validations (Single vs Recurring)
      if (this.isRecurring) {
        if (this.loadingPreview || !this.previewList || !this.previewList.length) {
          return false
        }
        const nonSkippedItems = this.previewList.filter((item) => !item.skipped)
        if (nonSkippedItems.length === 0) return false

        for (const item of nonSkippedItems) {
          const empId = item.selectedEmployeeId
          if (!empId || empId === "unassigned" || empId === "none") {
            return false
          }
          if (!this.isEmployeeEligible(empId)) return false
          const effTime = item.adjustedTime || item.originalTime || this.localStart
          if (!effTime) {
            return false
          }
          if (item.status === "collision" || item.status === "absent" || item.status === "no_shiftplan" || item.status === "restricted") {
            return false
          }
        }
      } else {
        if (!this.localColId) {
          return false
        }
        if (!this.isEmployeeEligible(this.localColId)) return false
      }

      return true
    },
    isNoteRequired() {
      return !!this.data?.template?.requiresNote
    },
    noteLabel() {
      return this.isNoteRequired ? "Notiz (Pflichtfeld) *" : "Notiz (Optional)"
    },
    conflictRules() {
      return [
        { text: "Immer die gleiche Person", value: "same" },
        { text: "Automatisch zuteilen", value: "auto" },
        { text: "Personen selbst zuteilen", value: "helper" },
      ]
    },
  },
  methods: {
    appointmentRestriction() {
      const source = this.data?.template?.appointmentRestriction || {}
      return {
        excludeApprentices: source.excludeApprentices === true,
        excludedEmployees: (source.excludedEmployees || []).map((employee) => String(employee?._id || employee || "")).filter(Boolean),
      }
    },
    isEmployeeEligible(employeeOrId) {
      const template = this.data?.template || {}
      if (template.type === "task" || template.type === "pause" || template.isPause || template.specialType === "pause") return true

      const employeeId = String(employeeOrId?.id || employeeOrId?._id || employeeOrId || "")
      if (this.isOnlineMeeting) {
        if (!employeeId || ["ma_info", "no_show", "unassigned", "none"].includes(employeeId)) return false
        const employee = typeof employeeOrId === "object" ? employeeOrId : (this.employees || []).find((candidate) => String(candidate.id) === employeeId)
        if (String(employee?.dbId || employee?.id || employeeId) === String(userService.getUserId() || "")) return false
      }
      if (!employeeId || ["ma_info", "no_show", "unassigned", "none"].includes(employeeId)) return true

      const restriction = this.appointmentRestriction()
      if (restriction.excludedEmployees.includes(employeeId)) return false
      if (!restriction.excludeApprentices) return true

      const employee =
        typeof employeeOrId === "object"
          ? employeeOrId
          : (this.employees || []).find((candidate) => String(candidate?.id || candidate?._id || "") === employeeId)
      return employee?.isApprentice !== true
    },
    employeeRestrictionMessage(employeeOrId) {
      if (this.isEmployeeEligible(employeeOrId)) return ""
      if (this.isOnlineMeeting) return "Bitte einen anderen Mitarbeiter auswählen. Du nimmst als Ersteller bereits teil."
      const employeeId = String(employeeOrId?.id || employeeOrId?._id || employeeOrId || "")
      const restriction = this.appointmentRestriction()
      if (restriction.excludedEmployees.includes(employeeId)) return "Diese Person ist für diese Terminart gesperrt."
      return "Diese Terminart ist für Lernende gesperrt."
    },
    ensureEligibleEmployeeSelection() {
      if (this.isEmployeeEligible(this.localColId)) return
      const nextEmployee = this.eligibleEmployees.find((employee) => !["ma_info", "no_show", "unassigned"].includes(String(employee.id)))
      this.localColId = nextEmployee?.id || this.eligibleEmployees[0]?.id || null
    },
    resetTacCredits() {
      this.tacCreditsRequestId += 1
      if (this.tacCreditsTimeout) {
        clearTimeout(this.tacCreditsTimeout)
        this.tacCreditsTimeout = null
      }
      this.tacCreditsLoading = false
      this.tacCreditsError = ""
      this.tacCreditsUnfiltered = false
      this.tacCreditCards = []
      this.selectedTacPackageId = null
      this.tacActivityTemplateId = null
      this.tacBillingEnabled = false
    },
    createTacRequestId() {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return `calendar:${crypto.randomUUID()}`
      }
      return `calendar:${Date.now()}:${Math.random().toString(36).slice(2)}`
    },
    normalizeTacPersonName(value) {
      return String(value || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
    },
    localEmployeeForTacResource(resource) {
      const wanted = this.normalizeTacPersonName(resource?.name)
      if (!wanted) return null
      return (this.employees || []).find((employee) => this.normalizeTacPersonName(employee?.name) === wanted) || null
    },
    applyTacResourceEmployee(resource) {
      const employee = this.localEmployeeForTacResource(resource)
      if (!employee) {
        this.$toast.error(`TAC-Mitarbeiter „${resource?.name || "Unbekannt"}“ wurde im Kalender nicht gefunden.`)
        return
      }
      this.localColId = employee.id
    },
    applyTacAvailability(slot) {
      if (!slot) return
      this.selectedTacPackageId = slot.packageId
      if (slot.date) this.selectedDate = slot.date
      if (slot.start) this.localStart = slot.start
      const card = this.tacCreditCards.find((entry) => String(entry.packageId) === String(slot.packageId))
      if (card?.serviceDurationMinutes) this.localDuration = Number(card.serviceDurationMinutes)
      const resource = slot.resources?.[0]
      if (resource) this.applyTacResourceEmployee(resource)
      this.loadTacCreditsDebounced(50)
    },
    loadTacCreditsDebounced(delay = 300) {
      if (this.tacCreditsTimeout) clearTimeout(this.tacCreditsTimeout)
      this.tacCreditsTimeout = setTimeout(() => {
        this.tacCreditsTimeout = null
        this.loadTacCredits()
      }, delay)
    },
    async loadTacCredits() {
      if (!this.tacServiceType || !this.selectedCustomerId || !this.selectedDate || !this.localStart) {
        this.resetTacCredits()
        return
      }
      const requestId = ++this.tacCreditsRequestId
      const previousPackageId = this.selectedTacPackageId
      this.tacCreditsLoading = true
      this.tacCreditsError = ""
      try {
        const response = await api.shiftManagement.fetchTacCredits(this.selectedCustomerId, {
          serviceType: this.tacServiceType,
          appointmentDate: this.selectedDate,
          startTime: this.localStart,
          templateId: this.data?.template?._id || this.data?.template?.id,
          templateTitle: this.data?.template?.title,
        })
        if (requestId !== this.tacCreditsRequestId) return
        this.tacCreditCards = Array.isArray(response?.cards) ? response.cards : []
        this.tacActivityTemplateId = response?.activityTemplateId || null
        this.tacBillingEnabled = response?.billingEnabled === true
        const previous = this.tacCreditCards.find((card) => String(card.packageId) === String(previousPackageId))
        const preferred = previous || this.tacCreditCards.find((card) => card.exactAvailable) || this.tacCreditCards[0]
        this.selectedTacPackageId = preferred?.packageId || null
        if (preferred?.serviceDurationMinutes) this.localDuration = Number(preferred.serviceDurationMinutes)
      } catch (error) {
        if (requestId !== this.tacCreditsRequestId) return
        this.tacCreditCards = []
        this.selectedTacPackageId = null
        this.tacCreditsError =
          error?.response?.data?.message || error?.response?.data || error?.message || "Die TAC-Packagebuchungen konnten nicht geladen werden."
      } finally {
        if (requestId === this.tacCreditsRequestId) this.tacCreditsLoading = false
      }
    },
    async resolveTacProfileIdForHistoryDebug() {
      if (this.selectedCustomerTacProfileId) {
        return {
          tacProfileId: this.selectedCustomerTacProfileId,
          lookupResponse: null,
        }
      }

      const customer = this.selectedCustomer
      if (!customer || typeof customer !== "object") {
        console.log("[TAC SELECTED CUSTOMER DEBUG]", customer)
        throw new Error("Die Kundensuche hat weder eine TAC-ID noch verwertbare Kundendaten geliefert.")
      }

      const nestedUser = customer.user && typeof customer.user === "object" ? customer.user : {}
      const firstName = String(
        customer.vorname || customer.firstName || customer.firstname || nestedUser.vorname || nestedUser.firstName || nestedUser.firstname || "",
      ).trim()
      const lastName = String(
        customer.nachname || customer.lastName || customer.lastname || nestedUser.nachname || nestedUser.lastName || nestedUser.lastname || "",
      ).trim()
      const email = String(customer.email || customer.mail || nestedUser.email || nestedUser.mail || "").trim()

      if (!lastName && !email) {
        console.log("[TAC SELECTED CUSTOMER DEBUG]", customer)
        throw new Error("Die Kundensuche liefert keine TAC-ID und auch keinen Nachnamen bzw. keine E-Mail für den TAC-Lookup.")
      }

      const lookupRequest = {}
      if (firstName) lookupRequest.FirstName = firstName
      if (lastName) lookupRequest.LastName = lastName
      if (email) lookupRequest.EMail = email

      const lookupResponse = await api.shiftManagement.requestTac("post", "Profile/GetProfiles", lookupRequest)
      const profiles = Array.isArray(lookupResponse?.Profiles?.Profile)
        ? lookupResponse.Profiles.Profile
        : lookupResponse?.Profiles?.Profile
        ? [lookupResponse.Profiles.Profile]
        : []
      const candidates = profiles
        .map((profile) => {
          const profileData = profile?.Profile || profile
          return {
            tacProfileId: this.tacUniqueIdValue(profileData?.IDs || profileData?.ProfileIDs || profile?.ProfileIDs),
            profile,
          }
        })
        .filter((candidate) => candidate.tacProfileId)

      const lookupDiagnostics = {
        selectedCustomer: customer,
        request: lookupRequest,
        response: lookupResponse,
        candidates,
      }
      window.__TAC_PROFILE_LOOKUP_DEBUG__ = lookupDiagnostics
      console.log("[TAC PROFILE LOOKUP DEBUG]", lookupDiagnostics)

      if (!candidates.length) {
        throw new Error("TAC hat für diesen Kunden keine Profil-ID geliefert. Details stehen in der Browserkonsole.")
      }
      if (candidates.length > 1) {
        throw new Error(`TAC hat ${candidates.length} passende Profile geliefert. Aus Sicherheitsgründen wurde keines automatisch gewählt.`)
      }

      return {
        tacProfileId: candidates[0].tacProfileId,
        lookupResponse,
      }
    },
    async loadTacPackageHistoryDebug() {
      this.tacPackageHistoryLoading = true
      try {
        const profileResolution = await this.resolveTacProfileIdForHistoryDebug()
        const tacProfileId = profileResolution.tacProfileId
        const memberDetailResponse = await api.shiftManagement.requestTac(
          "post",
          "Membership/MemberDetail",
          this.tacMemberDetailBody({
            ProfileIDs: {
              UniqueID: [{ source: "TAC", value: tacProfileId }],
            },
          }),
        )
        const selectedStudio = this.$store?.state?.studioStore?.selectedStudio || null
        const studioResortId = String(selectedStudio?.tacResortId || selectedStudio?.tac_resort_id || selectedStudio?.tac?.resortId || "").trim()
        const profileResortId = String(profileResolution.lookupResponse?.ResortId || "").trim()
        const memberPropertyId = this.tacUniqueIdValue(memberDetailResponse?.MemberDetail?.PropertyID)
        const resortIds = [...new Set([studioResortId, profileResortId, memberPropertyId].filter(Boolean))]
        if (!resortIds.length) {
          throw new Error("TAC hat weder im Profil-Lookup noch in MemberDetail eine Resort-ID geliefert.")
        }

        const broadTimeSpan = {
          Start: "2000-01-01T00:00:00",
          End: "2035-12-31T23:59:59",
        }
        const historyScenarios = [
          { key: "without_time_span", filterByCreationDate: false },
          { key: "booking_date", filterByCreationDate: false, TimeSpan: broadTimeSpan },
          { key: "creation_date", filterByCreationDate: true, TimeSpan: broadTimeSpan },
        ]
        const historyRequests = resortIds.flatMap((candidateResortId) =>
          historyScenarios.map((scenario) => ({
            resortId: candidateResortId,
            scenario,
          })),
        )
        const historyResults = await Promise.allSettled(
          historyRequests.map(({ resortId: candidateResortId, scenario }) =>
            api.shiftManagement.requestTac("post", "Booking/ProfileBookingHistory", {
              limitToRequestedProfile: true,
              maxResults: 1000,
              ignoreInvoicesForBookings: false,
              withBookingItems: true,
              filterByCreationDate: scenario.filterByCreationDate,
              withTemporary: false,
              withCancelled: true,
              ...(scenario.TimeSpan ? { TimeSpan: scenario.TimeSpan } : {}),
              Conditions: {
                Conditions: [],
              },
              ProfileIDs: {
                UniqueID: [{ source: "TAC", value: tacProfileId }],
              },
              ResortId: candidateResortId,
            }),
          ),
        )
        const historyAttempts = historyResults.map((result, index) => ({
          resortId: historyRequests[index].resortId,
          scenario: historyRequests[index].scenario.key,
          response:
            result.status === "fulfilled"
              ? result.value
              : {
                  requestFailed: true,
                  error: result.reason?.response?.data || result.reason?.message || String(result.reason),
                },
        }))
        const successfulHistory = historyAttempts.find((attempt) => attempt.response?.Result?.resultStatusFlag === "SUCCESS")
        const resortId = successfulHistory?.resortId || resortIds[0]
        const historyResponse = successfulHistory?.response || historyAttempts[0]?.response || null
        const diagnostics = {
          tacProfileId,
          profileLookup: {
            result: profileResolution.lookupResponse?.Result || null,
            resortId: profileResolution.lookupResponse?.ResortId || null,
          },
          selectedStudio: selectedStudio
            ? {
                id: selectedStudio._id || selectedStudio.id || null,
                name: selectedStudio.name || null,
                tacResortId: selectedStudio.tacResortId || selectedStudio.tac_resort_id || null,
                tacPropertyId: selectedStudio.tacPropertyId || selectedStudio.tac_property_id || null,
              }
            : null,
          studioResortId,
          profileResortId,
          memberPropertyId,
          testedResortIds: resortIds,
          resortId,
          memberDetailResult: memberDetailResponse?.Result || null,
          historyResponse,
          historyAttempts,
        }
        window.__TAC_PACKAGE_HISTORY_DEBUG__ = diagnostics
        console.log("[TAC PACKAGE HISTORY DEBUG]")
        console.log(JSON.stringify(diagnostics, null, 2))
        this.$toast.success("Package-History geladen – Antwort steht in der Browserkonsole.")
      } catch (error) {
        const details = error?.response?.data || error?.message || String(error)
        console.error("[TAC PACKAGE HISTORY DEBUG]", details)
        this.$toast.error(error?.response?.data?.message || error?.message || "Die TAC-Package-History konnte nicht geladen werden.")
      } finally {
        this.tacPackageHistoryLoading = false
      }
    },
    tacUniqueIdValue(value) {
      const ids = Array.isArray(value?.UniqueID) ? value.UniqueID : value?.UniqueID ? [value.UniqueID] : []
      const match = ids.find((entry) => String(entry?.source || "").toUpperCase() === "TAC") || ids[0]
      return match?.value === undefined || match?.value === null ? "" : String(match.value)
    },
    tacAccessPassDetails(response) {
      const details = []
      const visit = (value) => {
        if (!value) return
        if (Array.isArray(value)) {
          value.forEach(visit)
          return
        }
        if (typeof value !== "object") return
        if (value.AccessPassID) {
          details.push(value)
          visit(value.GroupAccessPassDetails?.AccessPassDetail)
          return
        }
        if (value.AccessPassDetail) visit(value.AccessPassDetail)
      }
      visit(response?.AccessPassDetails)
      visit(response?.AccessPassDetail)
      visit(response?.AccessPasses)
      visit(response?.MemberDetail?.AccessPassDetails)
      visit(response?.MemberDetail?.AccessPassDetail)
      visit(response?.MemberDetail?.AccessPasses)
      return details
    },
    tacAmountValue(value) {
      const parsed = Number(String(value?.value ?? value ?? 0).replace(",", "."))
      return Number.isFinite(parsed) ? parsed : 0
    },
    tacTextValues(value) {
      const values = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value]
      return values
        .map((entry) => (entry && typeof entry === "object" ? entry.value ?? entry.TextElement ?? entry.text : entry))
        .filter((entry) => entry !== undefined && entry !== null && String(entry).trim())
        .map(String)
    },
    tacTimeSpanDurationMinutes(timeSpan) {
      const start = Date.parse(timeSpan?.Start || "")
      const end = Date.parse(timeSpan?.End || "")
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
      return Math.round((end - start) / 60000)
    },
    tacLocalDateTime(date, time, addMinutes = 0) {
      const [hours, minutes] = String(time || "")
        .slice(0, 5)
        .split(":")
        .map(Number)
      if (!date || !Number.isFinite(hours) || !Number.isFinite(minutes)) return ""
      const value = new Date(`${date}T00:00:00`)
      value.setMinutes(hours * 60 + minutes + Number(addMinutes || 0))
      const pad = (part) => String(part).padStart(2, "0")
      return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}:00`
    },
    tacTemplateSummaries(response) {
      const templates = Array.isArray(response?.Templates?.Template)
        ? response.Templates.Template
        : response?.Templates?.Template
        ? [response.Templates.Template]
        : []
      return templates.map((template) => {
        const names = [
          ...this.tacTextValues(template?.Name),
          ...this.tacTextValues(template?.Description),
          template?.Shortname,
          template?.InfoText,
        ].filter(Boolean)
        return {
          id: this.tacUniqueIdValue(template?.TemplateIDs),
          type: template?.type || template?.Type || null,
          name: names[0] || "",
          names,
        }
      })
    },
    tacTemplateMatchesService(template) {
      const normalized = String(template?.names?.join(" ") || template?.name || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
      if (this.tacServiceType === "massage") return normalized.includes("massage")
      return normalized.includes("personal training") || normalized.includes("personaltraining") || /(^| )pt( |$)/.test(normalized)
    },
    tacBookingCreditGroups(response) {
      const bookings = Array.isArray(response?.Bookings?.Booking)
        ? response.Bookings.Booking
        : response?.Bookings?.Booking
        ? [response.Bookings.Booking]
        : []
      const groups = new Map()
      bookings.forEach((booking) => {
        const names = this.tacTextValues(booking?.Name)
        if (!this.tacTemplateMatchesService({ names })) return
        const references = Array.isArray(booking?.ReferenceBookingIDs?.UniqueID)
          ? booking.ReferenceBookingIDs.UniqueID
          : booking?.ReferenceBookingIDs?.UniqueID
          ? [booking.ReferenceBookingIDs.UniqueID]
          : []
        references.forEach((reference) => {
          if (String(reference?.source || "").toUpperCase() !== "TAC" || !reference?.value) return
          const referenceBookingId = String(reference.value)
          const group = groups.get(referenceBookingId) || {
            referenceBookingId,
            name: names[0] || `${this.tacServiceLabel}-Paket`,
            templateId: this.tacUniqueIdValue(booking?.TemplateIDs),
            totalBookings: null,
            usedBookings: 0,
            remainingBookings: null,
            durationMinutes: null,
            usedBookingIds: [],
          }
          const bookingId = this.tacUniqueIdValue(booking?.BookingIDs)
          const hasScheduledTime = Boolean(booking?.TimeSpan?.Start)
          if (hasScheduledTime) {
            group.usedBookings += 1
            group.durationMinutes = group.durationMinutes || this.tacTimeSpanDurationMinutes(booking?.TimeSpan)
            if (bookingId) group.usedBookingIds.push(bookingId)
          }
          groups.set(referenceBookingId, group)
        })
      })
      return [...groups.values()]
    },
    tacBookingHistorySummaries(response) {
      const items = Array.isArray(response?.BookingHistoryItems?.BookingHistoryItem)
        ? response.BookingHistoryItems.BookingHistoryItem
        : response?.BookingHistoryItems?.BookingHistoryItem
        ? [response.BookingHistoryItems.BookingHistoryItem]
        : []
      return items
        .map((item) => {
          const booking = item?.BookingHistoryItemDetail?.Booking || {}
          const names = [...this.tacTextValues(item?.Name), ...this.tacTextValues(booking?.Name)]
          const uniqueIds = (value) => {
            const entries = Array.isArray(value?.UniqueID) ? value.UniqueID : value?.UniqueID ? [value.UniqueID] : []
            return entries.filter((entry) => entry?.value).map((entry) => ({ source: entry.source || null, value: String(entry.value) }))
          }
          return {
            type: item?.type || booking?.type || null,
            name: names[0] || "",
            names,
            templateId: this.tacUniqueIdValue(item?.TemplateIDs || booking?.TemplateIDs),
            bookingIds: uniqueIds(booking?.BookingIDs),
            referenceBookingIds: uniqueIds(booking?.ReferenceBookingIDs),
            parentBookingIds: uniqueIds(booking?.ParentBookingIDs),
            packageBookingStatus: booking?.PackageBookingStatus || null,
            timeSpan: booking?.TimeSpan || null,
            bookingReferenceRph: item?.BookingReferenceRPH ?? null,
            invoiceItemReference: item?.InvoiceItemReference || null,
          }
        })
        .filter((item) => this.tacTemplateMatchesService(item))
    },
    normalizeDirectBookingCredits(groups) {
      return groups.map((group) => ({
        accessPassId: `booking-reference:${group.referenceBookingId}`,
        referenceBookingId: group.referenceBookingId,
        activityTemplateId: group.templateId,
        name: `${group.name} (${group.usedBookings} verwendet · Gesamtzahl noch unbekannt)`,
        remaining: null,
        validFrom: null,
        validUntil: null,
        validForAppointment: true,
        canDebit: false,
      }))
    },
    tacRemainingAccesses(detail) {
      const accounts = Array.isArray(detail?.Accounts?.Account) ? detail.Accounts.Account : detail?.Accounts?.Account ? [detail.Accounts.Account] : []
      const membershipAccounts = accounts.filter((account) => String(account?.type || "").toUpperCase() === "MEMBERSHIP")
      const relevantAccounts = membershipAccounts.length ? membershipAccounts : accounts
      const accountBalance = relevantAccounts.reduce((sum, account) => sum + this.tacAmountValue(account?.Balance), 0)
      const accountRemaining = this.tacAmountValue(detail?.CreditLimit) + accountBalance
      const possibleAccesses = Number(detail?.AccessInformation?.PossibleAccesses)
      const usedAccesses = Number(detail?.AccessInformation?.UsedAccesses || 0)
      const possibleBonusAccesses = Number(detail?.AccessInformation?.PossibleBonusAccesses || 0)
      const usedBonusAccesses = Number(detail?.AccessInformation?.UsedBonusAccesses || 0)
      const accessRemaining =
        Number.isFinite(possibleAccesses) && possibleAccesses > 0 ? possibleAccesses + possibleBonusAccesses - usedAccesses - usedBonusAccesses : null
      const remaining = accountRemaining !== 0 || accessRemaining === null ? accountRemaining : accessRemaining
      return Math.round((remaining + Number.EPSILON) * 100) / 100
    },
    normalizeDirectTacCards(response) {
      const seen = new Set()
      return this.tacAccessPassDetails(response)
        .map((detail) => {
          const accessPassId = this.tacUniqueIdValue(detail?.AccessPassID)
          const multiTemplateId = this.tacUniqueIdValue(detail?.MultiAccessPassTemplateID)
          const singleTemplateId = this.tacUniqueIdValue(detail?.SingleAccessPassTemplateID)
          const remaining = this.tacRemainingAccesses(detail)
          const validFrom = detail?.AccessPassStartDate || null
          const validUntil = detail?.AccessPassEndDate || null
          const appointmentDate = this.selectedDate || this.currentDate
          const validForAppointment =
            (!validFrom || appointmentDate >= String(validFrom).slice(0, 10)) && (!validUntil || appointmentDate <= String(validUntil).slice(0, 10))
          return {
            accessPassId,
            multiTemplateId,
            singleTemplateId,
            name: detail?.AccessPassTemplateMapping || detail?.AccessPassNumber || `TAC Karte ${multiTemplateId || singleTemplateId || accessPassId}`,
            remaining,
            validFrom,
            validUntil,
            validForAppointment,
            canDebit: Boolean(accessPassId) && validForAppointment && detail?.AccessPassCheckinAllowed !== false && remaining >= 1,
          }
        })
        .filter((card) => {
          if (!card.accessPassId || seen.has(card.accessPassId)) return false
          seen.add(card.accessPassId)
          return true
        })
    },
    tacMemberDetailBody(memberInfo) {
      return {
        withProfile: true,
        withOpenAccountTransactions: true,
        withPossibleIdlePeriodAmount: true,
        withOpenAccessPasses: true,
        withAccessPassCheckinTime: true,
        returnMode: "RETURN_ALL",
        withOpenBookings: false,
        withMultiAccessPassTimePeriods: true,
        withDetailedAccessPassCheckins: true,
        withGroupAccessPassAccountDetails: true,
        MemberInfo: memberInfo,
      }
    },
    async resolveDirectTacProfileId() {
      if (this.selectedCustomerTacProfileId) return this.selectedCustomerTacProfileId
      if (!this.selectedCustomerEmail) throw new Error("Beim ausgewählten Kunden fehlt die TAC-ID und die E-Mail-Adresse.")
      const response = await api.shiftManagement.requestTac("post", "Profile/VerifyAccount", {
        Account: { AccountName: this.selectedCustomerEmail },
      })
      const tacProfileId = this.tacUniqueIdValue(response?.Result?.IDs)
      if (!tacProfileId) {
        const message = response?.Result?.Text?.TextElement || response?.Result?.code
        throw new Error(message ? `TAC-Profil nicht gefunden: ${JSON.stringify(message)}` : "TAC-Profil nicht gefunden.")
      }
      return tacProfileId
    },
    async loadTacCreditsDebugDeprecated() {
      if (!this.tacServiceType || !this.selectedCustomerId) {
        this.resetTacCredits()
        return
      }
      const requestId = ++this.tacCreditsRequestId
      this.tacCreditsLoading = true
      this.tacCreditsError = ""
      this.tacCreditsUnfiltered = false
      this.tacCreditCards = []
      this.selectedTacAccessPassId = null
      try {
        const tacProfileId = await this.resolveDirectTacProfileId()
        const byProfileId = await api.shiftManagement.requestTac(
          "post",
          "Membership/MemberDetail",
          this.tacMemberDetailBody({
            ProfileIDs: {
              UniqueID: [{ source: "TAC", value: tacProfileId }],
            },
          }),
        )
        const diagnostics = { tacProfileId, byProfileId }
        let response = byProfileId
        const memberDetail = byProfileId?.MemberDetail || {}
        const propertyId = this.tacUniqueIdValue(memberDetail?.PropertyID)

        if (!this.tacAccessPassDetails(byProfileId).length) {
          const lookups = []
          const contractCustomerId = this.tacUniqueIdValue(memberDetail?.ContractCustomerID)
          const contractId = this.tacUniqueIdValue(memberDetail?.ContractID)
          const memberNumber = String(memberDetail?.MemberNumber || "").trim()

          if (contractCustomerId) {
            lookups.push({
              key: "byContractCustomerId",
              memberInfo: {
                ContractCustomerID: {
                  UniqueID: [{ source: "TAC", value: contractCustomerId }],
                },
              },
            })
          }
          if (contractId) {
            lookups.push({
              key: "byContractId",
              memberInfo: {
                ContractID: {
                  UniqueID: [{ source: "TAC", value: contractId }],
                },
              },
            })
          }
          if (memberNumber) lookups.push({ key: "byMemberNumber", memberInfo: { MemberNumber: memberNumber } })

          const results = await Promise.allSettled(
            lookups.map((lookup) => api.shiftManagement.requestTac("post", "Membership/MemberDetail", this.tacMemberDetailBody(lookup.memberInfo))),
          )
          results.forEach((result, index) => {
            diagnostics[lookups[index].key] =
              result.status === "fulfilled"
                ? result.value
                : { requestFailed: true, error: result.reason?.response?.data || result.reason?.message || String(result.reason) }
          })
          const responseWithCards = Object.values(diagnostics).find((value) => this.tacAccessPassDetails(value).length)
          if (responseWithCards) response = responseWithCards
        }

        if (propertyId) {
          const prepaidParams = new URLSearchParams({
            ProfileId: tacProfileId,
            ProfileIdSource: "TAC",
            ResortId: propertyId,
          })
          const activityTemplateParams = new URLSearchParams({
            Type: "ACTIVITY",
            ResortId: propertyId,
            IgnoreValidTimes: "true",
            WithAvailableResources: "false",
          })
          const packageTemplateParams = new URLSearchParams({
            Type: "PACKAGE",
            ResortId: propertyId,
            IgnoreValidTimes: "true",
            WithAvailableResources: "false",
          })
          const additionalLookups = [
            {
              key: "prepaidCards",
              promise: api.shiftManagement.requestTac("get", `GiftCertificate/PrepaidCard?${prepaidParams.toString()}`),
            },
            {
              key: "activityTemplateCatalogRaw",
              promise: api.shiftManagement.requestTac("get", `Configuration/TemplateInfo?${activityTemplateParams.toString()}`),
            },
            {
              key: "packageTemplateCatalogRaw",
              promise: api.shiftManagement.requestTac("get", `Configuration/TemplateInfo?${packageTemplateParams.toString()}`),
            },
            {
              key: "activityBookings",
              promise: api.shiftManagement.requestTac("post", "Booking/GetBookings", {
                withTemporary: false,
                withCancelled: true,
                withRevenueDetails: true,
                withTimeDetails: true,
                withTimeCreditCheck: false,
                Type: "ACTIVITY",
                ProfileIDs: {
                  UniqueID: [{ source: "TAC", value: tacProfileId }],
                },
                TimeSpan: {
                  Start: "2024-01-01T00:00:00",
                  End: "2027-12-31T23:59:59",
                },
                ResortId: propertyId,
              }),
            },
            {
              key: "allActivityBookingsWithoutTimeFilter",
              promise: api.shiftManagement.requestTac("post", "Booking/GetBookings", {
                withTemporary: false,
                withCancelled: false,
                withRevenueDetails: true,
                withTimeDetails: true,
                withTimeCreditCheck: false,
                Type: "ACTIVITY",
                ProfileIDs: {
                  UniqueID: [{ source: "TAC", value: tacProfileId }],
                },
                ResortId: propertyId,
              }),
            },
            {
              key: "packageBookings",
              promise: api.shiftManagement.requestTac("post", "Booking/GetBookings", {
                withTemporary: false,
                withCancelled: true,
                withRevenueDetails: true,
                withTimeDetails: true,
                withTimeCreditCheck: false,
                Type: "PACKAGE",
                ProfileIDs: {
                  UniqueID: [{ source: "TAC", value: tacProfileId }],
                },
                ResortId: propertyId,
              }),
            },
            {
              key: "profileBookingHistory",
              promise: api.shiftManagement.requestTac("post", "Booking/ProfileBookingHistory", {
                limitToRequestedProfile: true,
                maxResults: 1000,
                ignoreInvoicesForBookings: false,
                withBookingItems: true,
                filterByCreationDate: false,
                withTemporary: false,
                withCancelled: true,
                ProfileIDs: {
                  UniqueID: [{ source: "TAC", value: tacProfileId }],
                },
                ResortId: propertyId,
              }),
            },
          ]
          const additionalResults = await Promise.allSettled(additionalLookups.map((lookup) => lookup.promise))
          additionalResults.forEach((result, index) => {
            diagnostics[additionalLookups[index].key] =
              result.status === "fulfilled"
                ? result.value
                : { requestFailed: true, error: result.reason?.response?.data || result.reason?.message || String(result.reason) }
          })
          diagnostics.bookingCreditGroups = this.tacBookingCreditGroups(diagnostics.allActivityBookingsWithoutTimeFilter)
          diagnostics.profileBookingHistorySummaries = this.tacBookingHistorySummaries(diagnostics.profileBookingHistory)

          const availabilityDate = this.selectedDate || this.currentDate
          const sortedPackageGroups = [...diagnostics.bookingCreditGroups].sort((left, right) => right.usedBookings - left.usedBookings)
          const packageAvailabilityCandidates = [sortedPackageGroups[0], sortedPackageGroups[sortedPackageGroups.length - 1]].filter(
            (group, index, groups) =>
              group?.referenceBookingId &&
              group?.templateId &&
              groups.findIndex((entry) => entry?.referenceBookingId === group.referenceBookingId) === index,
          )
          const packageAvailabilityRequests = packageAvailabilityCandidates.map((group) => {
            const requestedStart = this.tacLocalDateTime(availabilityDate, this.localStart)
            const durationMinutes =
              group.durationMinutes ||
              this.tacTimeSpanDurationMinutes({
                Start: this.tacLocalDateTime(availabilityDate, this.localStart),
                End: this.tacLocalDateTime(availabilityDate, this.calculatedEnd),
              }) ||
              60
            return {
              group,
              requestedStart,
              requestedEnd: this.tacLocalDateTime(availabilityDate, this.localStart, durationMinutes),
              searchStart: this.tacLocalDateTime(availabilityDate, "00:00"),
              searchEnd: this.tacLocalDateTime(availabilityDate, "23:59"),
              durationMinutes,
            }
          })
          const packageAvailabilityResults = await Promise.allSettled(
            packageAvailabilityRequests.map(({ group, searchStart, searchEnd }) =>
              api.shiftManagement.requestTac("post", "Booking/Availability", {
                channel: "WELLNESSBOOKING",
                Availabilities: {
                  Availability: [
                    {
                      blockTime: false,
                      exactStartTimeCheck: false,
                      TemplateIDs: {
                        UniqueID: [{ source: "TAC", value: group.templateId }],
                      },
                      ReferenceBookingIDs: {
                        UniqueID: [{ source: "TAC", value: group.referenceBookingId }],
                      },
                      Profiles: {
                        Profile: [
                          {
                            Profile: {
                              ProfileIDs: {
                                UniqueID: [{ source: "TAC", value: tacProfileId }],
                              },
                            },
                          },
                        ],
                      },
                      NumberOfPersons: 1,
                      TimeSpans: {
                        TimeSpan: [{ Start: searchStart, End: searchEnd }],
                      },
                      BookingLifetime: 0,
                      ResultsLimit: 0,
                      ResortId: propertyId,
                    },
                  ],
                },
              }),
            ),
          )
          diagnostics.packageAvailabilityChecks = packageAvailabilityResults.map((result, index) => ({
            group: packageAvailabilityRequests[index].group,
            request: {
              requestedStart: packageAvailabilityRequests[index].requestedStart,
              requestedEnd: packageAvailabilityRequests[index].requestedEnd,
              searchStart: packageAvailabilityRequests[index].searchStart,
              searchEnd: packageAvailabilityRequests[index].searchEnd,
              durationMinutes: packageAvailabilityRequests[index].durationMinutes,
              exactStartTimeCheck: false,
              blockTime: false,
              bookingLifetime: 0,
            },
            response:
              result.status === "fulfilled"
                ? result.value
                : { requestFailed: true, error: result.reason?.response?.data || result.reason?.message || String(result.reason) },
          }))
          diagnostics.packageAvailabilityOptions = diagnostics.packageAvailabilityChecks.flatMap((entry) => {
            const availabilities = Array.isArray(entry?.response?.Availabilities?.Availability)
              ? entry.response.Availabilities.Availability
              : entry?.response?.Availabilities?.Availability
              ? [entry.response.Availabilities.Availability]
              : []
            return availabilities.map((availability) => {
              const resources = Array.isArray(availability?.Resources?.Resource)
                ? availability.Resources.Resource
                : availability?.Resources?.Resource
                ? [availability.Resources.Resource]
                : []
              return {
                referenceBookingId: entry.group.referenceBookingId,
                templateId: this.tacUniqueIdValue(availability?.TemplateIDs),
                start: availability?.TimeSpan?.Start || null,
                end: availability?.TimeSpan?.End || null,
                resortId: availability?.ResortId || null,
                resources: resources.map((resource) => ({
                  category: resource?.category || null,
                  id: this.tacUniqueIdValue(resource?.ResourceID),
                  name: resource?.Resource || resource?.Shortname || "",
                })),
              }
            })
          })

          const packageInstanceLookups = diagnostics.bookingCreditGroups.slice(0, 20).map((group) => {
            const params = new URLSearchParams({
              PackageId: group.referenceBookingId,
              ResortId: propertyId,
              TemplateDetail: "false",
              WithTemporary: "true",
              WithPackageBillingItems: "true",
              WithRevenueDetails: "true",
            })
            return {
              group,
              promise: api.shiftManagement.requestTac("get", `Booking/Package?${params.toString()}`),
            }
          })
          const packageInstanceResults = await Promise.allSettled(packageInstanceLookups.map((lookup) => lookup.promise))
          diagnostics.bookingPackagesByReferenceId = packageInstanceResults.map((result, index) => ({
            group: packageInstanceLookups[index].group,
            response:
              result.status === "fulfilled"
                ? result.value
                : { requestFailed: true, error: result.reason?.response?.data || result.reason?.message || String(result.reason) },
          }))

          const activityBookings = Array.isArray(diagnostics.activityBookings?.Bookings?.Booking)
            ? diagnostics.activityBookings.Bookings.Booking
            : diagnostics.activityBookings?.Bookings?.Booking
            ? [diagnostics.activityBookings.Bookings.Booking]
            : []
          const referenceUsage = new Map()
          activityBookings.forEach((booking) => {
            const ids = Array.isArray(booking?.ReferenceBookingIDs?.UniqueID)
              ? booking.ReferenceBookingIDs.UniqueID
              : booking?.ReferenceBookingIDs?.UniqueID
              ? [booking.ReferenceBookingIDs.UniqueID]
              : []
            ids.forEach((entry) => {
              if (String(entry?.source || "").toUpperCase() !== "TAC" || !entry?.value) return
              const referenceBookingId = String(entry.value)
              const usage = referenceUsage.get(referenceBookingId) || {
                referenceBookingId,
                usedBookings: 0,
                childBookingIds: [],
              }
              usage.usedBookings += 1
              const childBookingId = this.tacUniqueIdValue(booking?.BookingIDs)
              if (childBookingId) usage.childBookingIds.push(childBookingId)
              referenceUsage.set(referenceBookingId, usage)
            })
          })
          diagnostics.referenceUsage = [...referenceUsage.values()]
          const referenceLookups = diagnostics.referenceUsage.slice(0, 20).map((usage) => {
            const params = new URLSearchParams({
              BookingId: usage.referenceBookingId,
              ResortId: propertyId,
            })
            return {
              usage,
              promise: api.shiftManagement.requestTac("get", `Booking?${params.toString()}`),
            }
          })
          const referenceResults = await Promise.allSettled(referenceLookups.map((lookup) => lookup.promise))
          diagnostics.referenceBookings = referenceResults.map((result, index) => ({
            usage: referenceLookups[index].usage,
            response:
              result.status === "fulfilled"
                ? result.value
                : { requestFailed: true, error: result.reason?.response?.data || result.reason?.message || String(result.reason) },
          }))
          const referenceTemplateMap = new Map()
          diagnostics.referenceBookings.forEach((entry) => {
            const parents = Array.isArray(entry?.response?.Bookings?.Booking)
              ? entry.response.Bookings.Booking
              : entry?.response?.Bookings?.Booking
              ? [entry.response.Bookings.Booking]
              : []
            parents.forEach((parent) => {
              const templateId = this.tacUniqueIdValue(parent?.TemplateIDs)
              if (!templateId || referenceTemplateMap.has(templateId)) return
              referenceTemplateMap.set(templateId, {
                id: templateId,
                type: parent?.type || null,
                names: this.tacTextValues(parent?.Name),
              })
            })
          })
          diagnostics.referenceBookingTemplates = [...referenceTemplateMap.values()]
          const referencePackageLookups = diagnostics.referenceBookingTemplates.map((template) => {
            const params = new URLSearchParams({
              ProfileId: tacProfileId,
              TemplateId: template.id,
              ResortId: propertyId,
              TemplateDetail: "true",
              WithTemporary: "false",
              WithPackageBillingItems: "true",
            })
            return {
              template,
              promise: api.shiftManagement.requestTac("get", `Booking/Package?${params.toString()}`),
            }
          })
          const referencePackageResults = await Promise.allSettled(referencePackageLookups.map((lookup) => lookup.promise))
          diagnostics.bookingPackagesByReferenceTemplate = referencePackageResults.map((result, index) => ({
            template: referencePackageLookups[index].template,
            response:
              result.status === "fulfilled"
                ? result.value
                : { requestFailed: true, error: result.reason?.response?.data || result.reason?.message || String(result.reason) },
          }))

          const activityTemplates = this.tacTemplateSummaries(diagnostics.activityTemplateCatalogRaw)
          const packageTemplates = this.tacTemplateSummaries(diagnostics.packageTemplateCatalogRaw)
          diagnostics.activityTemplateCatalogResult = diagnostics.activityTemplateCatalogRaw?.Result || null
          diagnostics.packageTemplateCatalogResult = diagnostics.packageTemplateCatalogRaw?.Result || null
          diagnostics.activityTemplates = activityTemplates
          diagnostics.relevantActivityTemplates = activityTemplates.filter((template) => this.tacTemplateMatchesService(template))
          diagnostics.packageTemplates = packageTemplates
          diagnostics.relevantPackageTemplates = packageTemplates.filter((template) => this.tacTemplateMatchesService(template))
          delete diagnostics.activityTemplateCatalogRaw
          delete diagnostics.packageTemplateCatalogRaw

          const packageLookups = diagnostics.relevantPackageTemplates
            .filter((template) => template.id)
            .slice(0, 20)
            .map((template) => {
              const params = new URLSearchParams({
                ProfileId: tacProfileId,
                TemplateId: template.id,
                ResortId: propertyId,
                TemplateDetail: "true",
                WithTemporary: "false",
                WithPackageBillingItems: "true",
              })
              return {
                template,
                promise: api.shiftManagement.requestTac("get", `Booking/Package?${params.toString()}`),
              }
            })
          const packageResults = await Promise.allSettled(packageLookups.map((lookup) => lookup.promise))
          diagnostics.bookingPackagesByTemplate = packageResults.map((result, index) => ({
            template: packageLookups[index].template,
            response:
              result.status === "fulfilled"
                ? result.value
                : { requestFailed: true, error: result.reason?.response?.data || result.reason?.message || String(result.reason) },
          }))
        } else {
          diagnostics.additionalLookupSkipped = "MemberDetail enthält keine PropertyID."
        }

        window.__TAC_ACCESS_PASS_DEBUG__ = diagnostics
        console.log("[TAC ACCESS PASS DEBUG] Direkte Antworten über /api/tac/api:")
        console.log(JSON.stringify(diagnostics, null, 2))

        if (requestId !== this.tacCreditsRequestId) return
        this.tacCreditCards = this.normalizeDirectTacCards(response)
        if (!this.tacCreditCards.length) {
          this.tacCreditCards = this.normalizeDirectBookingCredits(diagnostics.bookingCreditGroups || [])
        }
        this.tacCreditsUnfiltered = this.tacCreditCards.length > 0
        const preferred = this.tacCreditCards.find((card) => card.canDebit)
        this.selectedTacAccessPassId = preferred?.accessPassId || null
      } catch (error) {
        if (requestId !== this.tacCreditsRequestId) return
        this.tacCreditsError =
          error?.response?.data?.message || error?.response?.data || error?.message || "Die direkte TAC-Abfrage ist fehlgeschlagen."
      } finally {
        if (requestId === this.tacCreditsRequestId) this.tacCreditsLoading = false
      }
    },
    fetchPreviewDebounced() {
      if (!this.isRecurring) {
        this.previewList = []
        return
      }
      if (this.previewTimeout) {
        clearTimeout(this.previewTimeout)
      }
      this.previewTimeout = setTimeout(() => {
        this.fetchPreview()
      }, 400)
    },
    async fetchPreview() {
      if (!this.isRecurring) return
      this.loadingPreview = true
      try {
        const payload = {
          date: this.selectedDate || this.data?.date || this.currentDate,
          start: this.localStart,
          end: this.calculatedEnd,
          duration: this.localDuration,
          assignedEmployee: this.localColId,
          recurrence: {
            frequency: this.recurrence.frequency,
            interval: this.recurrence.interval,
            weekdays: this.recurrence.frequency === "weekly" ? this.recurrence.weekdays : [],
            endDate: this.recurrenceEndType === "date" ? this.recurrence.endDate : null,
            count: this.recurrenceEndType === "count" ? this.recurrence.count : null,
            flexibilityType: this.recurrenceFlexibility,
            flexibilityWindow: this.recurrenceFlexibilityWindow,
          },
          type: this.data?.template?.type === "task" ? "task" : "appointment",
          templateId: this.data?.template?._id || this.data?.template?.id || null,
        }
        const data = await api.shiftManagement.fetchSeriesPreview(payload)
        const primaryIdStr = this.localColId ? String(this.localColId) : ""
        this.previewList = (data || []).map((item) => {
          const assignedIdStr = item.assignedEmployee?.id ? String(item.assignedEmployee.id) : primaryIdStr
          return {
            ...item,
            selectedEmployeeId: assignedIdStr,
            skipped: false,
            backendAdjustedTime: item.adjustedTime,
            backendStatus: item.status,
            originalAdjustedTime: item.adjustedTime,
          }
        })
        this.applyGlobalConflictRule()
      } catch (e) {
        console.error("Error fetching preview:", e)
      } finally {
        this.loadingPreview = false
      }
    },
    applyGlobalConflictRule() {
      if (!this.previewList || !this.previewList.length) return
      const primaryIdStr = this.localColId ? String(this.localColId) : ""
      this.previewList.forEach((item) => {
        if (this.globalConflictRule === "same") {
          this.$set(item, "selectedEmployeeId", primaryIdStr)
          this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
          this.$set(item, "isAdjusted", !!item.adjustedTime)
          this.$set(item, "status", item.backendStatus || "available")
          this.$set(item, "skipped", false)
        } else if (this.globalConflictRule === "auto") {
          if (item.status === "available" && item.selectedEmployeeId === primaryIdStr) {
            // Keep as is
          } else if (item.backendStatus === "available") {
            this.$set(item, "selectedEmployeeId", primaryIdStr)
            this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
            this.$set(item, "isAdjusted", !!item.adjustedTime)
            this.$set(item, "status", "available")
            this.$set(item, "skipped", false)
          } else {
            // Find the first alternative that is valid for auto-assignment under global flexibility rules
            const alt = item.alternatives ? item.alternatives.find((a) => a.validForAutoAssign) : null
            if (alt) {
              this.$set(item, "selectedEmployeeId", String(alt.id))
              this.$set(item, "adjustedTime", alt.adjustedTime || null)
              this.$set(item, "isAdjusted", !!alt.adjustedTime)
              this.$set(item, "status", "available")
              this.$set(item, "skipped", false)
            } else {
              this.$set(item, "selectedEmployeeId", primaryIdStr)
              this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
              this.$set(item, "isAdjusted", !!item.adjustedTime)
              this.$set(item, "status", item.backendStatus || "available")
              this.$set(item, "skipped", false)
            }
          }
        } else if (this.globalConflictRule === "skip") {
          if (item.backendStatus === "available") {
            this.$set(item, "selectedEmployeeId", primaryIdStr)
            this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
            this.$set(item, "isAdjusted", !!item.adjustedTime)
            this.$set(item, "status", "available")
            this.$set(item, "skipped", false)
          } else {
            this.$set(item, "skipped", true)
          }
        } else if (this.globalConflictRule === "helper") {
          if (item.backendStatus === "available") {
            this.$set(item, "selectedEmployeeId", primaryIdStr)
            this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
            this.$set(item, "isAdjusted", !!item.adjustedTime)
            this.$set(item, "status", "available")
            this.$set(item, "skipped", false)
          } else {
            this.$set(item, "selectedEmployeeId", "unassigned")
            this.$set(item, "adjustedTime", null)
            this.$set(item, "isAdjusted", false)
            this.$set(item, "status", "available")
            this.$set(item, "skipped", false)
          }
        }
      })
    },
    onPreviewEmployeeChange(item) {
      const selectedId = item.selectedEmployeeId
      const isHelper = selectedId === "ma_info" || selectedId === "no_show" || selectedId === "unassigned"
      if (isHelper) {
        this.$set(item, "adjustedTime", null)
        this.$set(item, "isAdjusted", false)
        this.$set(item, "status", "available")
        return
      }

      const primaryIdStr = this.localColId ? String(this.localColId) : ""
      if (selectedId === primaryIdStr) {
        this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
        this.$set(item, "isAdjusted", !!item.adjustedTime)
        this.$set(item, "status", item.backendStatus || "available")
        return
      }

      const alt = item.alternatives ? item.alternatives.find((a) => String(a.id) === selectedId) : null
      if (alt) {
        const bestTime = alt.adjustedTime || null
        this.$set(item, "adjustedTime", bestTime)
        this.$set(item, "isAdjusted", !!bestTime)
        this.$set(item, "status", "available")
      } else {
        this.$set(item, "adjustedTime", null)
        this.$set(item, "isAdjusted", false)
        this.$set(item, "status", item.backendStatus === "no_shiftplan" ? "no_shiftplan" : "collision")
      }
    },
    onPreviewTimeChange(item, newTime) {
      if (newTime === item.originalTime) {
        this.$set(item, "adjustedTime", null)
        this.$set(item, "isAdjusted", false)
      } else {
        this.$set(item, "adjustedTime", newTime)
        this.$set(item, "isAdjusted", true)
      }

      const selectedId = item.selectedEmployeeId
      const isHelper = selectedId === "ma_info" || selectedId === "no_show" || selectedId === "unassigned"
      if (isHelper) {
        this.$set(item, "status", "available")
        return
      }

      let slots = []
      const primaryIdStr = this.localColId ? String(this.localColId) : ""
      if (selectedId === primaryIdStr) {
        slots = item.freeSlots || []
      } else {
        const alt = item.alternatives ? item.alternatives.find((a) => String(a.id) === selectedId) : null
        if (alt) {
          slots = alt.availableSlots || []
        }
      }

      if (slots.includes(newTime)) {
        this.$set(item, "status", "available")
      } else if (newTime === item.originalTime && selectedId === primaryIdStr) {
        this.$set(item, "status", item.backendStatus || "available")
      } else {
        this.$set(item, "status", item.backendStatus === "no_shiftplan" ? "no_shiftplan" : "collision")
      }
    },
    getAvailableTimeOptions(item) {
      const selectedId = item.selectedEmployeeId
      const isHelper = selectedId === "ma_info" || selectedId === "no_show" || selectedId === "unassigned"
      if (isHelper) {
        return [item.originalTime]
      }

      let slots = []
      const primaryIdStr = this.localColId ? String(this.localColId) : ""
      if (selectedId === primaryIdStr) {
        slots = item.freeSlots || []
      } else {
        const alt = item.alternatives ? item.alternatives.find((a) => String(a.id) === selectedId) : null
        if (alt) {
          slots = alt.availableSlots || []
        }
      }

      const list = [...slots]
      if (!list.includes(item.originalTime)) {
        list.push(item.originalTime)
      }
      list.sort()
      return list
    },
    getPreviewEmployeeOptions(item) {
      return this.eligibleEmployees.map((emp) => {
        const empIdStr = emp.id ? String(emp.id) : ""
        const isHelper = empIdStr === "ma_info" || empIdStr === "no_show" || empIdStr === "unassigned"
        let suffix = ""
        let textClass = ""

        const assignedIdStr = item.assignedEmployee?.id ? String(item.assignedEmployee.id) : ""

        if (isHelper) {
          suffix = ""
          textClass = "primary--text font-weight-medium"
        } else if (assignedIdStr && empIdStr === assignedIdStr && (item.backendStatus === "available" || item.originalAdjustedTime)) {
          suffix = " (Frei)"
          textClass = "success--text font-weight-bold"
        } else if (item.alternatives && item.alternatives.some((alt) => alt.id && String(alt.id) === empIdStr)) {
          suffix = " (Frei)"
          textClass = "success--text font-weight-bold"
        } else {
          suffix = " (Kollision/Abwesend)"
          textClass = "grey--text"
        }

        return {
          text: (emp.name || emp.firstName || "Mitarbeiter") + suffix,
          value: empIdStr,
          class: textClass,
        }
      })
    },
    getPreviewItemStyle(item) {
      if (item.skipped) {
        return {
          borderColor: "rgba(244, 67, 54, 0.15)",
          background: "rgba(244, 67, 54, 0.03)",
        }
      }
      if (item.status === "collision") {
        return {
          borderColor: "rgba(255, 152, 0, 0.2)",
          background: "rgba(255, 152, 0, 0.03)",
        }
      }
      if (item.status === "no_shiftplan") {
        return {
          borderColor: "rgba(255, 193, 7, 0.2)",
          background: "rgba(255, 193, 7, 0.03)",
        }
      }
      if (item.status === "absent") {
        return {
          borderColor: "rgba(244, 67, 54, 0.15)",
          background: "rgba(244, 67, 54, 0.03)",
        }
      }
      return {}
    },
    formatPreviewDate(dateStr) {
      if (!dateStr) return ""
      return moment(dateStr, "YYYY-MM-DD").locale("de").format("dd. DD.MM.YYYY")
    },
    getCollisionTooltip(conflict) {
      if (!conflict) return ""
      return `Kollision mit: ${conflict.title || "Termin"} (${conflict.start} - ${conflict.end})`
    },
    updateCustomer(value) {
      this.selectedCustomer = value
      this.resetTacCredits()
      if (this.tacServiceType && this.selectedCustomerId) this.$nextTick(() => this.loadTacCredits())
    },
    updateCustomer2(value) {
      this.selectedCustomer2 = value
    },
    addSecondPerson() {
      this.showSecondPerson = true
      this.localDuration = this.baseDuration + 30
    },
    removeSecondPerson() {
      this.showSecondPerson = false
      this.selectedCustomer2 = null
      this.noAccountMode2 = false
      this.noAccountInfo2 = { firstName: "", lastName: "", phone: "", email: "" }
      this.localDuration = this.baseDuration
    },
    toggleFinder() {
      if (!this.canUseFinder) return
      this.showFinder = !this.showFinder
    },
    onFinderSelectSlot(selection) {
      if (!selection) return
      if (!this.isEmployeeEligible(selection.colId)) {
        this.$toast.warning(this.employeeRestrictionMessage(selection.colId))
        return
      }
      this.finderSelection = { ...selection }
      this.selectedDate = selection.date
      this.localStart = selection.start
      this.localColId = selection.colId
      this.finderSelectedEmployee = {
        id: selection.colId,
        name: selection.colName || "Mitarbeiter",
        picture: selection.colPicture || null,
      }
    },
    onFinderSelectAndClose(selection) {
      if (selection) this.onFinderSelectSlot(selection)
      this.showFinder = false
    },
    formatDate(dateStr) {
      if (!dateStr) return ""
      return moment(dateStr, "YYYY-MM-DD").format("DD.MM.YYYY")
    },
    toggleWeekday(value) {
      const idx = this.recurrence.weekdays.indexOf(value)
      if (idx > -1) {
        if (this.recurrence.weekdays.length > 1) {
          this.recurrence.weekdays.splice(idx, 1)
        }
      } else {
        this.recurrence.weekdays.push(value)
      }
    },
    save() {
      if (!this.canSave) return
      if (this.isRecurring) {
        const nonSkippedItems = this.previewList.filter((item) => !item.skipped)
        if (!nonSkippedItems.length) {
          this.$toast.error("Bitte mindestens einen Termin in der Serie aktivieren.")
          return
        }
        if (nonSkippedItems.some((item) => !this.isEmployeeEligible(item.selectedEmployeeId))) {
          this.$toast.error("Mindestens eine Zuteilung ist für diese Terminart gesperrt.")
          return
        }

        const items = nonSkippedItems.map((item) => {
          const empId = item.selectedEmployeeId
          const isHelper = empId === "ma_info" || empId === "no_show" || empId === "unassigned"

          const empObj = this.employees.find((e) => String(e.id) === String(empId))
          const dbId = empObj ? empObj.dbId : null

          const itemTime = item.adjustedTime || this.localStart
          const endM = moment(`${item.date} ${itemTime}`, "YYYY-MM-DD HH:mm").add(this.localDuration, "minutes")

          if (this.data?.template?.type === "task") {
            return {
              date: item.date,
              time: itemTime,
              title: this.localTitle,
              description: this.localDesc,
              assignedEmployee: isHelper ? null : dbId,
              helperColumn: isHelper ? empId : "none",
              icon: this.localIcon,
              color: this.data.template.color,
              duration: this.localDuration,
              checklist: this.data.template.checklist ? JSON.parse(JSON.stringify(this.data.template.checklist)) : [],
              instructions: this.data.template.instructions || "",
              templateId: this.data.template._id || this.data.template.id,
            }
          } else {
            const apptData = {
              date: item.date,
              start: itemTime,
              end: endM.format("HH:mm"),
              title: this.localTitle,
              description: this.localDesc,
              assignedEmployee: isHelper ? null : dbId,
              helperColumn: isHelper ? empId : "none",
              customer: this.data?.template.requiresCustomer
                ? this.data?.template.specialType === "new_contract" && this.contractMode === "existing"
                  ? this.selectedCustomer
                  : this.noAccountMode
                  ? null
                  : this.selectedCustomer
                : null,
              customer2: this.showSecondPerson && !this.noAccountMode2 ? this.selectedCustomer2 : null,
              icon: this.localIcon,
              color: this.data.template.color,
              specialType: this.data.template.specialType || null,
              templateId: this.data.template._id || this.data.template.id,
            }

            if (this.noAccountMode && this.noAccountInfo.firstName) {
              apptData.noAccountData = { ...this.noAccountInfo }
            }
            if (this.showSecondPerson && this.noAccountMode2 && this.noAccountInfo2.firstName) {
              apptData.noAccountData2 = { ...this.noAccountInfo2 }
            }
            if (this.data?.template.specialType === "new_contract" && this.contractMode === "guest" && this.guestData.firstName) {
              apptData.rawData = { guestData: { ...this.guestData } }
            }
            return apptData
          }
        })

        this.$emit("save", {
          isBatch: true,
          items,
          type: this.data?.template?.type === "task" ? "task" : "appointment",
          extendSeriesId: this.data?.extendSeriesId || null,
        })
        return
      }

      if (!this.isEmployeeEligible(this.localColId)) {
        this.$toast.error(this.employeeRestrictionMessage(this.localColId))
        return
      }

      const payload = {
        ...this.data,
        colId: this.localColId,
        time: this.localStart,
        duration: this.localDuration,
        description: this.localDesc,
        date: this.selectedDate || this.data?.date || this.currentDate,
        template: {
          ...this.data.template,
          title: this.localTitle,
          icon: this.localIcon,
        },
        customer:
          this.isOnlineMeeting
            ? null
            : this.data?.template.specialType === "new_contract" && this.contractMode === "existing"
            ? this.selectedCustomer
            : this.noAccountMode
            ? null
            : this.selectedCustomer,
        noAccountData: this.noAccountMode ? { ...this.noAccountInfo } : null,
        guestData: this.data?.template.specialType === "new_contract" && this.contractMode === "guest" ? { ...this.guestData } : null,
        customer2: this.showSecondPerson && !this.noAccountMode2 ? this.selectedCustomer2 : null,
        noAccountData2: this.showSecondPerson && this.noAccountMode2 ? { ...this.noAccountInfo2 } : null,
        hasSecondPerson: this.showSecondPerson,
        recurrence: null,
        finderSelection: this.finderSelection ? { ...this.finderSelection } : null,
      }
      if (this.tacServiceType && this.selectedTacCard?.exactAvailable) {
        payload.tacBillingRequest = {
          serviceType: this.tacServiceType,
          packageId: String(this.selectedTacCard.packageId),
          requestId: this.tacBookingRequestId || this.createTacRequestId(),
          activityTemplateId: this.tacActivityTemplateId || this.selectedTacCard.activityTemplateId || null,
          resourceId: this.selectedTacResource?.id || null,
        }
      }
      this.$emit("save", payload)
    },
  },
}
</script>

<style scoped>
.wcd-card {
  background: rgba(22, 22, 22, 0.85) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4) !important;
}
.wcd-card.light-theme {
  background: rgba(255, 255, 255, 0.88) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
}
.wcd-card.light-theme .wcd-title {
  color: #1e293b !important;
}
.wcd-card.light-theme .wcd-subtitle {
  color: rgba(0, 0, 0, 0.6) !important;
}
.wcd-card.light-theme .wcd-time-label {
  color: rgba(0, 0, 0, 0.5) !important;
}
.wcd-card.light-theme .wcd-finder-toggle-bar {
  background: rgba(0, 0, 0, 0.04) !important;
  color: rgba(0, 0, 0, 0.7) !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.wcd-card.light-theme .wcd-finder-toggle-bar.active {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-color: #2e7d32 !important;
}
.wcd-tac-account-hint {
  padding: 8px 10px;
  border-radius: 9px;
  color: rgba(255, 255, 255, 0.65);
  background: rgba(33, 150, 243, 0.08);
  border: 1px solid rgba(33, 150, 243, 0.18);
  font-size: 0.75rem;
}
.wcd-tac-credit-card {
  padding: 12px;
  border-radius: 12px;
  background: rgba(33, 150, 243, 0.06);
  border: 1px solid rgba(33, 150, 243, 0.16);
}
.wcd-card.light-theme .wcd-tac-account-hint {
  color: rgba(0, 0, 0, 0.65);
  background: rgba(33, 150, 243, 0.06);
}
.wcd-card.light-theme .wcd-tac-credit-card {
  background: rgba(33, 150, 243, 0.04);
  border-color: rgba(33, 150, 243, 0.14);
}

.wcd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
}
.wcd-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.wcd-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: white;
  letter-spacing: -0.2px;
}
.wcd-subtitle {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 1px;
}

/* Time Row */
.wcd-time-row {
  display: flex;
  gap: 12px;
}
.wcd-time-item {
  flex: 1;
}
.wcd-time-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
}
.wcd-time-input >>> input {
  text-align: center;
  font-weight: 700;
  font-size: 0.9rem;
}
.wcd-time-input >>> .v-input__slot {
  border-radius: 10px !important;
  min-height: 38px !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  transition: all 0.25s;
}
.wcd-time-input >>> .v-input__slot:hover {
  border-color: rgba(255, 255, 255, 0.15) !important;
}
.light-theme .wcd-time-input >>> .v-input__slot {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.light-theme .wcd-time-input >>> .v-input__slot:hover {
  border-color: rgba(0, 0, 0, 0.18) !important;
}

.wcd-time-result {
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 800;
  color: #4caf50;
  background: rgba(76, 175, 80, 0.08);
  border-radius: 10px;
  border: 1px solid rgba(76, 175, 80, 0.15);
}

/* Toggle Buttons */
.wcd-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: none;
}
.wcd-card.light-theme .wcd-toggle-btn {
  color: rgba(0, 0, 0, 0.6);
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.wcd-toggle-btn.active {
  color: white !important;
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  border-color: #2e7d32 !important;
  box-shadow: 0 4px 10px rgba(76, 175, 80, 0.3) !important;
}
.wcd-card.light-theme .wcd-toggle-btn.active {
  color: white !important;
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  border-color: #2e7d32 !important;
  box-shadow: 0 4px 10px rgba(76, 175, 80, 0.3) !important;
}
.wcd-toggle-btn:hover {
  border-color: rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.04);
}
.wcd-card.light-theme .wcd-toggle-btn:hover {
  border-color: rgba(0, 0, 0, 0.15);
  background: rgba(0, 0, 0, 0.05);
}
.wcd-toggle-btn i {
  color: inherit !important;
}

/* Dual Person Badge */
.wcd-dual-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--v-primary-base) !important;
  background: color-mix(in srgb, var(--v-primary-base) 10%, transparent) !important;
  border: 1px solid color-mix(in srgb, var(--v-primary-base) 20%, transparent) !important;
}

/* Employee Dropdown Select */
.wcd-employee-select >>> .v-input__slot {
  border-radius: 10px !important;
  min-height: 38px !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  transition: all 0.25s;
}
.wcd-employee-select >>> .v-input__slot:hover {
  border-color: rgba(255, 255, 255, 0.15) !important;
}
.light-theme .wcd-employee-select >>> .v-input__slot {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.light-theme .wcd-employee-select >>> .v-input__slot:hover {
  border-color: rgba(0, 0, 0, 0.18) !important;
}
.wcd-employee-select >>> .v-select__selection {
  font-size: 0.85rem;
  font-weight: 600;
  color: inherit;
}

/* Save Button */
.wcd-save-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 0.85rem !important;
  padding: 8px 16px !important;
  transition: all 0.2s !important;
}
.wcd-save-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.35) !important;
}

/* Recurrence Panel Styles */
.wcd-recurring-panel {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.3s;
}
.light-theme .wcd-recurring-panel {
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.wcd-small-label {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 6px;
}
.light-theme .wcd-small-label {
  color: rgba(0, 0, 0, 0.5);
}
.wcd-toggle-group {
  background: transparent !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 10px !important;
  width: 100%;
  display: flex !important;
  overflow: hidden;
}
.light-theme .wcd-toggle-group {
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}
.wcd-toggle-btn-small {
  flex: 1;
  text-transform: none !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  letter-spacing: 0px !important;
  background: transparent !important;
  border: none !important;
  height: 32px !important;
  color: rgba(255, 255, 255, 0.55) !important;
}
.light-theme .wcd-toggle-btn-small {
  color: rgba(0, 0, 0, 0.6) !important;
}
.wcd-toggle-group >>> .v-btn--active {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
}
.light-theme .wcd-toggle-group >>> .v-btn--active {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
}
.wcd-numeric-input >>> input {
  text-align: center;
  font-weight: 700;
}
.wcd-numeric-input >>> .v-input__slot {
  border-radius: 8px !important;
  min-height: 34px !important;
  padding: 0 8px !important;
}
.wcd-date-input >>> .v-input__slot {
  border-radius: 8px !important;
  min-height: 34px !important;
}
.wcd-date-input >>> input {
  font-weight: 600;
  font-size: 0.8rem;
}
.wcd-radio-group >>> .v-radio {
  margin-bottom: 0px !important;
}
.overflow-visible {
  overflow: visible !important;
}
.gap-2 {
  gap: 8px;
}
.wcd-preview-item {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.25s ease;
}
.wcd-preview-item.light-theme-item {
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.wcd-preview-item.skipped {
  opacity: 0.6;
  background: rgba(244, 67, 54, 0.02) !important;
  border-color: rgba(244, 67, 54, 0.1) !important;
}
.wcd-preview-employee-select >>> .v-input__slot {
  border-radius: 8px !important;
  min-height: 28px !important;
  padding: 0 8px !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
}
.light-theme .wcd-preview-employee-select >>> .v-input__slot {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.wcd-preview-employee-select >>> .v-select__selection {
  font-size: 0.75rem;
  font-weight: 500;
}
</style>
